/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Drawer,
  Badge,
  LinearProgress,
  Pagination,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import TableChartIcon from '@mui/icons-material/TableChart';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckpointIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { formatStoredDateTime, formatStoredTime } from '../../utils/datetime';
import 'highlight.js/styles/github.css';
import { agentApi, accountApi } from '../../services/api';
import ABVOperatingControls from './ABVOperatingControls';
import type { BuilderOperatingProfile } from '../../types/agent';
import { useAuth } from '../../context/AuthContext';
import type {
  AgentDetail,
  AgentExecution,
  ConversationSession,
  ConversationSessionListItem,
  BuilderArtifactWorkflowResponse,
  BuilderIntentItemPatchRequest,
  BuilderIntentLedgerEventRequest,
  BuilderIntentLedgerResponse,
  MessageAttachment,
  RestoreSnapshotOptions,
} from '../../types/agent';
import AIBuilderInsightsPanel from './AIBuilderInsightsPanel';
import { useAIBuilderExecution, type BuilderLifecycleProgress } from '../../hooks';
import {
  BUILDER_LIFECYCLE_STATUS_METADATA,
  BUILDER_LIFECYCLE_STEPPER,
  isBlockedLifecycleStatus,
  type BuilderLifecycleStatus,
  type BuilderLifecycleTone,
} from './builderLifecycle';
import type { ToolTraceMetadata } from '../../hooks/useAgentChatSocket';
import useVoiceChat from '../../hooks/useVoiceChat';
import SubAgentEventsTree from '../chat/SubAgentEventsTree';
import AttachmentMessage from '../chat/AttachmentMessage';
import { attachSubAgentEvent, flushPendingSubAgentEvents } from '../../utils/subAgentEvents';
import { MicButton, VoiceOverlay, VoiceSettings } from '../voice';
import BuilderPlanMessage from './BuilderPlanMessage';
import ABVWorkflowStages from './ABVWorkflowStages';
import { emptyABVSnapshot, loadABVSnapshot, type ABVSnapshot } from './abvWorkflowSnapshot';
import stripMarkdown from '../../utils/stripMarkdown';
import {
  getAudioInputDisabledReason,
  isAudioInputSupported,
} from '../../utils/audioInputSupport';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import {
  buildBuilderPlanActionView,
  buildBuilderPlanDiffView,
  buildBuilderPlanDetailView,
  buildBuilderPlanHealthView,
  buildBuilderPlanPreviewView,
  buildBuilderPlanRevisionView,
  buildBuilderPlanReviewView,
  buildBuilderPlanStatusView,
  extractBuilderPlanPayloadFromExecutionDetail,
  extractBuilderPlanPayloadFromExecutionPlan,
  extractBuilderStateFromExecutionDetail,
  extractBuilderStateFromExecutionPlan,
} from './aiBuilderPlanStatus';

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to get icon for attachment type
const getAttachmentIcon = (type: string) => {
  switch (type) {
    case 'IMAGE': return <ImageIcon fontSize="small" />;
    case 'DOCUMENT': return <DescriptionIcon fontSize="small" />;
    case 'CODE': return <CodeIcon fontSize="small" />;
    case 'DATA': return <TableChartIcon fontSize="small" />;
    case 'AUDIO': return <AudiotrackIcon fontSize="small" />;
    default: return <InsertDriveFileIcon fontSize="small" />;
  }
};

// Helper function to clean markdown from session titles
const cleanMarkdownTitle = (title: string): string => {
  if (!title) return title;
  
  // Remove markdown headers (###, ##, #)
  let cleaned = title.replace(/^#{1,6}\s+/gm, '');
  
  // Remove bold/italic markers
  cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1'); // **bold**
  cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1'); // *italic*
  cleaned = cleaned.replace(/__([^_]+)__/g, '$1'); // __bold__
  cleaned = cleaned.replace(/_([^_]+)_/g, '$1'); // _italic_
  
  // Remove code blocks
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1'); // `code`
  
  // Remove links but keep text [text](url)
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  return cleaned.trim();
};

const isScopedBuilderArtifactUpdate = (
  artifact: NonNullable<BuilderArtifactWorkflowResponse['artifact']>,
): boolean => {
  const designPackage = artifact.design_package || {};
  const updateType = String(
    artifact.update_contract?.update_type
      || designPackage.update_type
      || artifact.mutation_plan?.update_type
      || designPackage.mutation_plan?.update_type
      || ''
  ).trim().toLowerCase();
  return Boolean(
    designPackage.design_mode === 'existing_system_update'
      && updateType
      && updateType !== 'structural_redesign'
  );
};

interface AIBuilderProps {
  system: any; // AI System data
  agent: AgentDetail; // System builder agent
  onExpertCreated?: () => void | Promise<void>; // Callback when AI makes any changes (experts, schedules, system config, etc.)
  // Canvas props for embedding interactive canvas in insights panel
  nodes?: any[];
  edges?: any[];
  onNodesChange?: any;
  onEdgesChange?: any;
  onConnect?: any;
  onNodeClick?: (event: React.MouseEvent, node: any) => void;
  onAutoLayout?: () => void;
  expertsData?: any;
  addExpertToCanvas?: (expert: any) => void;
  setExpertFormOpen?: (open: boolean) => void;
  selectedNode?: any;
  setSelectedNode?: (node: any) => void;
  setNodes?: React.Dispatch<React.SetStateAction<any[]>>;
  setEdges?: React.Dispatch<React.SetStateAction<any[]>>;
  refetchExperts?: () => void;
  validationIssues?: any[];
  onSaveCanvas?: () => void | Promise<void>;
  isSavingCanvas?: boolean;
  onRunCanvas?: () => void | Promise<void>;
  isRunningCanvas?: boolean;
  canRunCanvas?: boolean;
  runDisabledReason?: string;
  expertRunStates?: Record<string, any>;
  onViewExpertRun?: (expertId: number) => void;
  nodeTypes?: any; // Custom node types for ReactFlow
  onExpertUpdate?: (expert: any | null) => void; // Callback when expert is updated
}

interface Message {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL_CALL';
  content: string;
  timestamp: Date;
  executionId?: string; // Track execution ID for each assistant message
  workflowStatus?: string; // Backend-owned workflow status (never inferred)
  snapshotMode?: 'NONE' | 'FULL' | 'LIGHTWEIGHT' | 'DIFF';
  hasSnapshot?: boolean;
  dbMessageId?: number; // Database ID for snapshot operations
  // Tool call fields
  toolName?: string;
  toolArgs?: Record<string, any>;
  toolResult?: any;
  toolSuccess?: boolean;
  toolCallId?: string; // anchors nested sub-agent events
  subAgentEvents?: any[]; // nested events emitted by delegated sub-agents
  trace?: Record<string, any> | null;
  traceId?: string | null;
  rootTraceId?: string | null;
  parentTraceId?: string | null;
}

/**
 * ToolCallCard — Collapsible tool call display for the Architect chat.
 * Shows tool name + status badge, expands on click to show arguments & result.
 */
const ToolCallCard: React.FC<{
  message: Message;
  isDark: boolean;
  showTechnicalDetails?: boolean;
}> = ({ message, isDark, showTechnicalDetails = true }) => {
  const [expanded, setExpanded] = useState(false);

  const isRunning = message.toolResult === undefined || message.toolResult === null;
  const isCancelled = message.toolResult?.data?.cancelled === true;
  const isSuccess = message.toolSuccess === true;
  const statusColor = isRunning
    ? 'warning.main'
    : isCancelled
      ? 'text.secondary'
      : isSuccess
        ? 'success.main'
        : 'error.main';
  const statusLabel = isRunning
    ? 'Running...'
    : isCancelled
      ? 'Stopped'
      : isSuccess
        ? 'Completed'
        : 'Failed';
  const statusIcon = isRunning ? '⏳' : isCancelled ? '■' : isSuccess ? '✓' : '✗';

  return (
    <Box sx={{ mb: 1, ml: 5 }}>
      <Paper
        onClick={() => showTechnicalDetails && setExpanded(!expanded)}
        sx={{
          cursor: showTechnicalDetails ? 'pointer' : 'default',
          userSelect: 'none',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          bgcolor: isDark
            ? 'rgba(33, 150, 243, 0.06)'
            : 'rgba(33, 150, 243, 0.04)',
          transition: 'all 0.15s ease',
          overflow: 'hidden',
          '&:hover': {
            borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Header — always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          {showTechnicalDetails && (
            <Typography
              sx={{
                fontSize: '10px',
                color: 'text.secondary',
                transition: 'transform 0.15s ease',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                lineHeight: 1,
              }}
            >
              ▶
            </Typography>
          )}
          <Typography sx={{ fontSize: '13px' }}>🔧</Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, flex: 1, fontSize: '0.8rem' }}
          >
            {message.toolName}
          </Typography>
          <Chip
            label={`${statusIcon} ${statusLabel}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 500,
              bgcolor: `color-mix(in srgb, ${isDark ? '#fff' : '#000'} 8%, transparent)`,
              color: statusColor,
              border: '1px solid',
              borderColor: statusColor,
            }}
          />
        </Box>

        {/* Expanded content */}
        {expanded && showTechnicalDetails && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              px: 1.5,
              py: 1,
            }}
          >
            {/* Arguments */}
            {message.toolArgs && Object.keys(message.toolArgs).length > 0 && (
              <Box sx={{ mb: message.toolResult ? 1.5 : 0 }}>
                <Typography
                  variant="overline"
                  sx={{ fontSize: '0.6rem', color: 'text.secondary', letterSpacing: '0.05em' }}
                >
                  Arguments
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: 150,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                  }}
                >
                  {JSON.stringify(message.toolArgs, null, 2)}
                </Box>
              </Box>
            )}

            {/* Result */}
            {message.toolResult && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ fontSize: '0.6rem', color: 'text.secondary', letterSpacing: '0.05em' }}
                >
                  Result
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: 200,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                  }}
                >
                  {JSON.stringify(message.toolResult, null, 2)}
                </Box>
              </Box>
            )}

            {/* Running indicator */}
            {isRunning && (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                Waiting for result...
              </Typography>
            )}

            {/* Nested sub-agent events */}
            {Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0 && (
              <SubAgentEventsTree events={message.subAgentEvents} />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/**
 * ToolCallGroup — Collapsible container grouping consecutive tool calls.
 * Shows a summary header "🔧 N tool calls" that toggles the list.
 */
const ToolCallGroup: React.FC<{
  toolMsgs: Message[];
  isDark: boolean;
  isStaff?: boolean;
}> = ({ toolMsgs, isDark, isStaff = false }) => {
  const [collapsed, setCollapsed] = useState(true);

  const allDone = toolMsgs.every(m => m.toolResult !== undefined && m.toolResult !== null);
  const anyFailed = toolMsgs.some(m => m.toolSuccess === false);
  const activitySummary = !allDone
    ? 'Working with data'
    : anyFailed
      ? 'Some checks need attention'
      : 'Checks complete';
  const headerColor = !allDone ? 'warning.main' : anyFailed ? 'error.main' : 'success.main';
  const headerIcon = !allDone ? '⏳' : anyFailed ? '⚠️' : '✓';
  const headerLabel = activitySummary;

  return (
    <Box sx={{ mb: 1, ml: 5 }}>
      <Paper
        sx={{
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          bgcolor: isDark ? 'rgba(33, 150, 243, 0.04)' : 'rgba(33, 150, 243, 0.03)',
          overflow: 'hidden',
        }}
      >
        {/* Group header */}
        <Box
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              color: 'text.secondary',
              transition: 'transform 0.15s ease',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              lineHeight: 1,
            }}
          >
            ▶
          </Typography>
          <Typography sx={{ fontSize: '13px' }}>🔧</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              Background activity
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {collapsed
                ? (isStaff ? 'Show technical details' : 'Show progress details')
                : `${toolMsgs.length} background step${toolMsgs.length !== 1 ? 's' : ''}`}
            </Typography>
          </Box>
          <Chip
            label={`${headerIcon} ${headerLabel}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 500,
              bgcolor: `color-mix(in srgb, ${isDark ? '#fff' : '#000'} 8%, transparent)`,
              color: headerColor,
              border: '1px solid',
              borderColor: headerColor,
            }}
          />
        </Box>

        {/* Collapsible tool call list */}
        {!collapsed && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 0.75,
            }}
          >
            {toolMsgs.map(msg => (
              <ToolCallCard
                key={msg.id}
                message={msg}
                isDark={isDark}
                showTechnicalDetails={isStaff}
              />
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const formatProgressElapsed = (startedAt?: string): string => {
  if (!startedAt) return '0s';
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  if (elapsedSeconds < 60) return `${elapsedSeconds}s`;
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const formatProgressCost = (value: number): string => (
  Number.isFinite(value) ? `$${value.toFixed(4)}` : '$0.0000'
);

const lifecycleToneColor = (tone: BuilderLifecycleTone) => {
  if (tone === 'success') return 'success.main';
  if (tone === 'warning') return 'warning.main';
  if (tone === 'error') return 'error.main';
  return 'primary.main';
};

const lifecycleAlertSeverity = (tone: BuilderLifecycleTone): 'info' | 'success' | 'warning' | 'error' => {
  if (tone === 'success') return 'success';
  if (tone === 'warning') return 'warning';
  if (tone === 'error') return 'error';
  return 'info';
};

type LifecycleStepState = 'pending' | 'current' | 'completed' | 'interrupted';

const lifecycleStepState = (
  status: BuilderLifecycleStatus,
  stepIndex: number
): LifecycleStepState => {
  const metadata = BUILDER_LIFECYCLE_STATUS_METADATA[status];
  const isMainPath = BUILDER_LIFECYCLE_STEPPER.includes(status);
  if (stepIndex < metadata.order) return 'completed';
  if (stepIndex > metadata.order) return 'pending';
  // Same order as the current status.
  if (isMainPath && status === 'APPLIED') return 'completed';
  if (isBlockedLifecycleStatus(status) || status === 'APPLYING_RETRYABLE') return 'interrupted';
  if (isMainPath && status === BUILDER_LIFECYCLE_STEPPER[stepIndex]) return 'current';
  // Non-stepper status (e.g. SUPERSEDED) sharing a step order: neutral mark.
  return 'current';
};

const lifecycleStepColor = (state: LifecycleStepState) => {
  if (state === 'completed') return 'success.main';
  if (state === 'interrupted') return 'error.main';
  if (state === 'current') return 'primary.main';
  return 'text.disabled';
};

/**
 * BuilderLifecycleCard — live projection of the backend-owned builder artifact
 * lifecycle. Renders only statuses from the builderLifecycle contract (stepper
 * over the real statuses with sub/failure states visible), the live operation
 * journal (labels shared with the saved-plan views), blockers for NEEDS_* /
 * FAILED_* states, and event-sourced token/cost/elapsed chips. The state is
 * revision-guarded upstream by the lifecycle reducer; this card never infers.
 */
export const BuilderLifecycleCard: React.FC<{
  lifecycle: BuilderLifecycleProgress;
  lastIssue?: string | null;
  assistantActivity: { message: string; detail: string | null } | null;
  isAwaitingExecutionStart: boolean;
  wsConnected: boolean;
  isStaff: boolean;
  currentExecutionId: string | null;
  currentIteration: number;
  maxIterations: number;
  totalTokens: number;
  totalCost: number;
  isDark: boolean;
}> = ({
  lifecycle,
  lastIssue = null,
  assistantActivity,
  isAwaitingExecutionStart,
  wsConnected,
  isStaff,
  currentExecutionId,
  currentIteration,
  maxIterations,
  totalTokens,
  totalCost,
  isDark,
}) => {
  const [journalExpanded, setJournalExpanded] = useState(false);
  const statusMetadata = BUILDER_LIFECYCLE_STATUS_METADATA[lifecycle.status];
  const blocked = isBlockedLifecycleStatus(lifecycle.status);
  const isMainPath = BUILDER_LIFECYCLE_STEPPER.includes(lifecycle.status);

  const operations = lifecycle.operations;
  const applyProgress = operations.total > 0
    ? (operations.completed / operations.total) * 100
    : null;
  const stepperProgress = (statusMetadata.order / Math.max(BUILDER_LIFECYCLE_STEPPER.length - 1, 1)) * 100;
  const progressValue = lifecycle.status === 'APPLIED'
    ? 100
    : Math.min(98, Math.max(8, applyProgress ?? stepperProgress));

  const journalEntries = [...operations.entries].reverse();
  const visibleEntries = journalExpanded ? journalEntries : journalEntries.slice(0, 4);
  const blockerMessages = lifecycle.blockers
    .map((blocker) => [blocker.code, blocker.message].filter(Boolean).join(': '))
    .filter(Boolean);
  if (blockerMessages.length === 0 && blocked && lastIssue) {
    blockerMessages.push(lastIssue);
  }

  return (
    <Paper
      data-testid="builder-lifecycle-card"
      sx={{
        mb: 2,
        ml: { xs: 0, sm: 5 },
        p: 1.5,
        border: '1px solid',
        borderColor: statusMetadata.tone === 'error'
          ? 'error.main'
          : statusMetadata.tone === 'warning'
            ? 'warning.main'
            : isDark ? 'rgba(144, 202, 249, 0.28)' : 'rgba(25, 118, 210, 0.22)',
        bgcolor: isDark ? 'rgba(33, 150, 243, 0.06)' : 'rgba(25, 118, 210, 0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        {statusMetadata.isTerminal ? (
          <Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 16, color: lifecycleToneColor(statusMetadata.tone), lineHeight: 1 }}>●</Typography>
          </Box>
        ) : (
          <CircularProgress size={18} thickness={5} color={statusMetadata.tone === 'error' ? 'error' : statusMetadata.tone === 'warning' ? 'warning' : 'primary'} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              data-testid="lifecycle-status-label"
              variant="body2"
              sx={{ fontWeight: 700, fontSize: '0.85rem', flex: 1, minWidth: 0, color: lifecycleToneColor(statusMetadata.tone) }}
            >
              {statusMetadata.label}
            </Typography>
            {wsConnected ? (
              <WifiIcon sx={{ fontSize: 15, color: 'success.main' }} />
            ) : (
              <WifiOffIcon sx={{ fontSize: 15, color: 'warning.main' }} />
            )}
          </Box>
          {(lifecycle.detail || assistantActivity?.detail || (isAwaitingExecutionStart && statusMetadata.order === 0 && 'Waiting for the AI worker to start.')) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {lifecycle.detail || assistantActivity?.detail || 'Waiting for the AI worker to start.'}
            </Typography>
          )}

          {/* Lifecycle stepper over the real backend statuses */}
          <Box
            data-testid="lifecycle-stepper"
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}
          >
            {BUILDER_LIFECYCLE_STEPPER.map((step, index) => {
              const state = lifecycleStepState(lifecycle.status, index);
              return (
                <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Box
                    data-testid={`lifecycle-step-${step}`}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: lifecycleStepColor(state),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: state === 'pending' ? 'text.secondary' : 'text.primary',
                      fontWeight: state === 'current' || state === 'interrupted' ? 600 : 400,
                    }}
                  >
                    {BUILDER_LIFECYCLE_STATUS_METADATA[step].label}
                  </Typography>
                  {index < BUILDER_LIFECYCLE_STEPPER.length - 1 && (
                    <Box sx={{ width: 10, height: 1, bgcolor: 'divider', mx: 0.25 }} />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Sub / failure status that interrupts a main-path step */}
          {!isMainPath && (
            <Chip
              data-testid="lifecycle-substatus"
              size="small"
              label={statusMetadata.label}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 500,
                mb: 1,
                color: lifecycleToneColor(statusMetadata.tone),
                border: '1px solid',
                borderColor: lifecycleToneColor(statusMetadata.tone),
                bgcolor: 'transparent',
              }}
            />
          )}

          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ height: 5, borderRadius: 1, mb: 1 }}
            color={statusMetadata.tone === 'error' ? 'error' : statusMetadata.tone === 'warning' ? 'warning' : 'primary'}
          />

          {/* User-action hint (e.g. AWAITING_APPROVAL -> approve in the review panel) */}
          {statusMetadata.actionHint && statusMetadata.requiresUserAction && (
            <Typography
              data-testid="lifecycle-action-hint"
              variant="caption"
              sx={{ display: 'block', mb: 1, color: lifecycleToneColor(statusMetadata.tone), fontWeight: 600 }}
            >
              {statusMetadata.actionHint}
            </Typography>
          )}

          {/* Blockers for NEEDS_* / FAILED_* states */}
          {blocked && blockerMessages.length > 0 && (
            <Alert data-testid="lifecycle-blockers" severity={lifecycleAlertSeverity(statusMetadata.tone)} sx={{ mt: 0.5, mb: 1, py: 0.25 }}>
              {blockerMessages.map((message) => (
                <Typography key={message} variant="caption" sx={{ display: 'block' }}>
                  {message}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Live operation journal (labels shared with the saved-plan views) */}
          {operations.entries.length > 0 && (
            <Box data-testid="lifecycle-journal" sx={{ mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {`Operations ${operations.completed}/${operations.total}`}
                </Typography>
                {operations.failed > 0 && (
                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                    {`${operations.failed} failed`}
                  </Typography>
                )}
                {journalEntries.length > 4 && (
                  <Button
                    size="small"
                    onClick={() => setJournalExpanded((value) => !value)}
                    sx={{ ml: 'auto', minHeight: 20, py: 0, px: 0.75, fontSize: '0.65rem' }}
                  >
                    {journalExpanded ? 'Show fewer' : `Show all ${journalEntries.length}`}
                  </Button>
                )}
              </Box>
              {visibleEntries.map((entry) => (
                <Box
                  key={entry.operationId || `${entry.toolName}-${entry.symbolicKey}-${entry.occurredAt}`}
                  data-testid="lifecycle-journal-entry"
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, py: 0.25 }}
                >
                  <Typography sx={{ fontSize: 12, lineHeight: '18px', flexShrink: 0 }}>
                    {entry.success ? '✓' : '⚠'}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: entry.success ? 'text.primary' : 'error.main',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.label || entry.toolName || entry.mutation || 'Operation'}
                    </Typography>
                    {entry.error && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'error.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.error}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Chip size="small" label={`Elapsed ${formatProgressElapsed(lifecycle.startedAt)}`} sx={{ height: 20, fontSize: '0.65rem' }} />
            {isStaff && (
              <>
                <Chip size="small" label={`Revision ${lifecycle.revision}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                {currentExecutionId && <Chip size="small" label={`Execution ${currentExecutionId.slice(0, 8)}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                <Chip size="small" label={`Iteration ${currentIteration}/${maxIterations}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                <Chip size="small" label={`Tokens ${(totalTokens || 0).toLocaleString()}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                <Chip size="small" label={`Cost ${formatProgressCost(totalCost || 0)}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * Groups consecutive TOOL_CALL messages together.
 * Returns an array of items: regular Message objects or { type: 'tool_group', id, msgs }.
 */
function groupMessagesForAIBuilder(messages: Message[]): Array<Message | { type: 'tool_group'; id: string; msgs: Message[] }> {
  const result: Array<Message | { type: 'tool_group'; id: string; msgs: Message[] }> = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'TOOL_CALL') {
      const group: Message[] = [];
      while (i < messages.length && messages[i].role === 'TOOL_CALL') {
        group.push(messages[i]);
        i++;
      }
      result.push({ type: 'tool_group', id: `tg-${group[0].id}`, msgs: group });
    } else {
      result.push(messages[i]);
      i++;
    }
  }
  return result;
}

const AIBuilder: React.FC<AIBuilderProps> = ({ 
  system, 
  agent, 
  onExpertCreated,
  // Canvas props
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onAutoLayout,
  expertsData,
  addExpertToCanvas,
  setExpertFormOpen,
  selectedNode,
  setSelectedNode,
  setNodes,
  setEdges,
  refetchExperts,
  validationIssues,
  onSaveCanvas,
  isSavingCanvas,
  onRunCanvas,
  isRunningCanvas,
  canRunCanvas,
  runDisabledReason,
  expertRunStates,
  onViewExpertRun,
  nodeTypes,
  onExpertUpdate,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isStaff = user?.is_staff === true;
  const systemBuilderAccess = useActionAccess(AI_ACTION_SURFACES.systemBuilderSend);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileTab, setMobileTab] = useState(0); // 0 = Chat, 1 = Insights
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedMessageIndex, setSelectedMessageIndex] = useState<number | null>(null); // Track which message's execution to view
  const [sessionId, setSessionId] = useState<string | null>(null); // Track conversation session
  const [currentSessionData, setCurrentSessionData] = useState<any>(null); // Track loaded session with snapshot
  const [snapshotView, setSnapshotView] = useState<{ messageId: string; snapshot: any } | null>(null);
  const [snapshotLoadingMessageId, setSnapshotLoadingMessageId] = useState<string | null>(null);
  const [sourceHighlightedMessageId, setSourceHighlightedMessageId] = useState<string | null>(null);
  const [builderIntentLedgerNotice, setBuilderIntentLedgerNotice] = useState<string | null>(null);
  const [isHistoricalSession, setIsHistoricalSession] = useState(false); // Track if viewing historical session
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false); // Track when loading a session
  const [sessionsPage, setSessionsPage] = useState(1); // Pagination for sessions
  const [updatingBuilderIntentItemId, setUpdatingBuilderIntentItemId] = useState<string | null>(null);
  const [builderArtifactAction, setBuilderArtifactAction] = useState<'approve' | 'apply' | null>(null);
  const sessionsPageSize = 10; // Show 10 sessions per page
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const chatInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const userStartedNewConversation = useRef(false); // Track if user explicitly clicked "New"
  const sessionIdRef = useRef<string | null>(null); // Ref to track sessionId for callbacks
  const completedExecutionMessagesRef = useRef<Set<string>>(new Set());
  const [abvRefresh, refreshABV] = useState(0);
  const [pendingApproval, setPendingApproval] = useState<{ sessionId: string; executionId: string | null } | null>(null);
  const approvalLock = useRef(false);
  const approvalRequestSession = useRef<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Buffer for sub-agent events that arrive before their parent TOOL_CALL
  // message has been committed to React state. Flushed on every state
  // update that introduces a new TOOL_CALL message.
  const pendingSubAgentEventsRef = useRef<Record<string, any[]>>({});

  // Keep ref in sync
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const shouldAutoLoadLatestSession = !sessionId && messages.length === 0 && !userStartedNewConversation.current;

  // File attachment state
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Restore from snapshot state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState<Message | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreSnackbar, setRestoreSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(35); // Chat panel percentage
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle resize drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Clamp between 20% and 80%
      const clampedWidth = Math.min(Math.max(newWidth, 20), 80);
      setLeftPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  // WebSocket-based execution hook (uses AgentChatConsumer for streaming)
  const {
    isConnected: wsConnected,
    isExecuting,
    isAwaitingExecutionStart,
    currentExecutionId: wsExecutionId,
    currentSessionId: wsSessionId,
    currentExecutionPlan,
    executionStatus,
    progress,
    currentIteration,
    maxIterations,
    expertProgress,
    error: wsError,
    totalCost,
    totalTokens,
    toolNames,
    executionResult,
    assistantActivity,
    lastIssue,
    lifecycleProgress,
    executeWithMessage,
    stopExecution,
    subscribeToExecution,
    reset: resetExecution,
    connect: connectWs,
    disconnect: disconnectWs,
    pendingConfirmation,
    confirmTool,
  } = useAIBuilderExecution({
    systemId: system.id,
    agentId: agent.id,
    autoConnect: true,
    onToolCall: (toolName, toolArgs, toolCallId, trace: ToolTraceMetadata = {}) => {
      // Add tool call message to chat
      setMessages((prev) => {
        const next: Message[] = [
          ...prev,
          {
            id: `tool-${Date.now()}-${toolName}`,
            role: 'TOOL_CALL',
            content: '',
            timestamp: new Date(),
            toolName,
            toolArgs,
            toolCallId,
            subAgentEvents: [],
            ...trace,
          },
        ];
        // Flush any sub-agent events that arrived before this parent.
        return flushPendingSubAgentEvents(next as any[], pendingSubAgentEventsRef.current) as typeof next;
      });
    },
    onToolResult: (toolName, result, toolCallId, trace: ToolTraceMetadata = {}) => {
      // Update the last matching tool call message with the result
      setMessages((prev) => {
        const updated = [...prev];
        // Prefer matching by toolCallId so parallel tool calls don't collide
        if (toolCallId) {
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].role === 'TOOL_CALL' && updated[i].toolCallId === toolCallId) {
              updated[i] = {
                ...updated[i],
                toolResult: result,
                toolSuccess: result?.success ?? true,
                ...trace,
              };
              return updated;
            }
          }
        }
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].role === 'TOOL_CALL' && updated[i].toolName === toolName && !updated[i].toolResult) {
            updated[i] = {
              ...updated[i],
              toolResult: result,
              toolSuccess: result?.success ?? true,
            };
            break;
          }
        }
        return updated;
      });

      // Trigger data refresh for tools that modify experts, schedules, or system data
      const dataModifyingTools = [
        'create_expert',
        'update_expert', 
        'delete_expert',
        'create_shared_input',
        'update_shared_input',
        'delete_shared_input',
        'restore_shared_input',
        'create_ai_schedule',
        'update_ai_schedule',
        'delete_ai_schedule',
        'create_agent_schedule',
        'update_agent_schedule',
        'delete_agent_schedule',
      ];

      if (dataModifyingTools.includes(toolName)) {
        console.log(`🔄 Tool ${toolName} completed, refreshing data...`);
        
        // Trigger the callback immediately after tool completion
        if (onExpertCreated) {
          onExpertCreated();
        }

        // Invalidate relevant queries
        queryClient.invalidateQueries({ 
          queryKey: ['ai-system', String(system.id)]
        });
        queryClient.invalidateQueries({ 
          queryKey: ['experts', system.id]
        });
        queryClient.invalidateQueries({ 
          queryKey: ['shared-inputs', system.id]
        });
        queryClient.invalidateQueries({ 
          queryKey: ['schedules', system.id]
        });
        queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
        queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });
      }
    },
    onSubAgentEvent: (event) => {
      // Nested event produced by a delegated sub-agent. Attach it to the
      // matching parent TOOL_CALL message so the UI renders it nested.
      // If the parent is not in state yet (race with tool_call event),
      // buffer the event and let onToolCall flush it when the parent
      // gets committed.
      setMessages((prev) => {
        const updated = attachSubAgentEvent(prev as any[], event) as typeof prev;
        if (updated === (prev as any)) {
          const parentId = event.parent_tool_call_id;
          const buf = pendingSubAgentEventsRef.current;
          if (!buf[parentId]) buf[parentId] = [];
          buf[parentId].push(event);
          console.warn(
            '[AIBuilder] Buffered sub-agent event (parent not committed yet)',
            { parentId, type: event.type, buffered: buf[parentId].length },
          );
          return prev;
        }
        return updated;
      });
    },
    onExecutionCompleted: async (execId, status) => {
      console.log(`[AIBuilder] Execution ${execId} completed with status: ${status}`);
      if (!execId) {
        console.warn('[AIBuilder] Ignoring completion event without execution id');
        return;
      }

      if (completedExecutionMessagesRef.current.has(execId)) {
        console.log('[AIBuilder] Completion message already rendered for:', execId);
        return;
      }
      completedExecutionMessagesRef.current.add(execId);
      
      // Fetch lightweight execution state to get the response without step/tool payloads
      try {
        const executionSummary = await agentApi.getExecutionSummary(execId);

        const currentSessionId = sessionIdRef.current;
        if (
          (currentSessionId && executionSummary.session_id && executionSummary.session_id !== currentSessionId) ||
          (!currentSessionId && userStartedNewConversation.current)
        ) {
          console.log('[AIBuilder] Ignoring completion for inactive conversation:', {
            executionId: execId,
            executionSessionId: executionSummary.session_id,
            currentSessionId,
          });
          return;
        }
        
        if (approvalRequestSession.current === executionSummary.session_id) {
          approvalLock.current = false;
          approvalRequestSession.current = null;
          setPendingApproval(null);
          setApprovalError(null);
        }
        setSnapshotLoading(true);
        refreshABV(value => value + 1);
        // Determine message content based on status
        let messageContent = '';
        if (status === 'CANCELLED' || status === 'STOPPED') {
          messageContent = '⚠️ Execution was stopped by user.';
        } else if (status === 'ERROR') {
          messageContent = `❌ Execution failed: ${executionSummary.error || 'Unknown error'}`;
        } else {
          messageContent = executionSummary.result?.response || 'Execution completed';
        }

        const workflowStatus = String(
          executionSummary.result?.workflow_status
          || executionSummary.result?.architect_builder_validator_runtime?.status
          || '',
        );
        const isDurableArtifactOnlyCompletion = (
          workflowStatus === 'durable_ui_apply_deferred'
        );

        // Refresh messages from the API to get real DB IDs and snapshot data
        const currentSid = sessionIdRef.current;
        let authoritativeSessionLoaded = false;
        if (currentSid) {
          try {
            const sessionData = await agentApi.getSession(currentSid);
            const refreshedMessages: Message[] = sessionData.messages.map((msg: any) => {
              if (msg.role === 'TOOL_CALL' || msg.role === 'tool_call') {
                return {
                  id: String(msg.id) || `tool-${Date.now()}-${Math.random()}`,
                  role: 'TOOL_CALL' as const,
                  content: '',
                  timestamp: new Date(msg.created_at),
                  toolName: msg.tool_name,
                  toolArgs: msg.tool_input,
                  toolResult: msg.tool_output,
                  toolSuccess: msg.success,
                  toolCallId: msg.tool_call_id || String(msg.id),
                  subAgentEvents: Array.isArray(msg.sub_agent_events) ? msg.sub_agent_events : [],
                  trace: msg.trace || null,
                  traceId: msg.trace_id || msg.trace?.trace_id || null,
                  rootTraceId: msg.root_trace_id || msg.trace?.root_trace_id || null,
                  parentTraceId: msg.parent_trace_id || msg.trace?.parent_trace_id || null,
                };
              }
              return {
                id: String(msg.id) || `msg-${Date.now()}-${Math.random()}`,
                role: msg.role,
                content: msg.content,
                timestamp: new Date(msg.created_at),
                executionId: msg.execution || undefined,
                snapshotMode: msg.snapshot_mode,
                hasSnapshot: msg.has_snapshot,
                dbMessageId: Number(msg.id) || 0,
              };
            });
            let lastAssistantIdx = -1;
            for (let index = refreshedMessages.length - 1; index >= 0; index -= 1) {
              const message = refreshedMessages[index];
              if (
                message.role === 'ASSISTANT' &&
                String(message.executionId || '') === String(execId)
              ) {
                lastAssistantIdx = index;
                break;
              }
            }
            if (lastAssistantIdx >= 0) {
              authoritativeSessionLoaded = true;
              // Stamp the already-fetched summary's workflow status onto the
              // persisted message so the card reflects backend truth even when
              // the local append path was skipped.
              if (workflowStatus) {
                refreshedMessages[lastAssistantIdx] = {
                  ...refreshedMessages[lastAssistantIdx],
                  workflowStatus,
                };
              }
              setMessages(refreshedMessages);
              setSelectedMessageIndex(lastAssistantIdx);
            }
          } catch (refreshError) {
            console.warn('Failed to refresh messages from API:', refreshError);
          }
        }

        // The persisted conversation is authoritative. Only render the execution
        // summary when the session cannot provide this execution's saved message.
        if (!authoritativeSessionLoaded) {
          setMessages((prev) => {
            const filtered = prev
              .filter(m => !m.id.startsWith('assistant-stopping-'))
              .map((m) => {
                if (
                  (status === 'CANCELLED' || status === 'STOPPED') &&
                  m.role === 'TOOL_CALL' &&
                  (m.toolResult === undefined || m.toolResult === null)
                ) {
                  return {
                    ...m,
                    toolResult: {
                      success: false,
                      data: { cancelled: true },
                      error: 'Execution cancelled by user.',
                    },
                    toolSuccess: false,
                  };
                }
                return m;
              });
            const newMessages: Message[] = [
              ...filtered,
              {
                id: `assistant-${Date.now()}`,
                role: 'ASSISTANT' as const,
                content: messageContent,
                timestamp: new Date(),
                executionId: execId,
                workflowStatus: workflowStatus || undefined,
              },
            ];
            setSelectedMessageIndex(newMessages.length - 1);
            return newMessages;
          });
        }

        // Refresh conversation and durable review state after the persisted reply
        // has been selected. Artifact-only turns did not mutate the live system.
        setSessionsPage(1);
        queryClient.invalidateQueries({
          queryKey: ['conversation-sessions', agent.id, system.id]
        });
        queryClient.invalidateQueries({
          queryKey: ['latest-conversation-session', agent.id, system.id]
        });
        queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
        queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });

        if (status === 'COMPLETED' && !isDurableArtifactOnlyCompletion) {
          if (onExpertCreated) {
            console.log('🔄 Triggering data refresh callback...');
            await onExpertCreated();
          }

          queryClient.invalidateQueries({
            queryKey: ['ai-system', String(system.id)]
          });
          queryClient.invalidateQueries({
            queryKey: ['experts', system.id]
          });
          queryClient.invalidateQueries({
            queryKey: ['shared-inputs', system.id]
          });
          queryClient.invalidateQueries({ queryKey: ['ai-systems'] });
        }
      } catch (error) {
        console.error('Failed to fetch execution details:', error);
      }
    },
    onError: (error) => {
      setApprovalError('status_unavailable');
      console.error('[AIBuilder] Execution error:', error);
      setMessages((prev) => {
        const closedPendingTools = prev.map((message) => {
          if (
            message.role === 'TOOL_CALL' &&
            (message.toolResult === undefined || message.toolResult === null)
          ) {
            return {
              ...message,
              toolResult: {
                success: false,
                data: null,
                error,
              },
              toolSuccess: false,
            };
          }
          return message;
        });

        return [
          ...closedPendingTools,
          {
            id: `error-${Date.now()}`,
            role: 'ASSISTANT',
            content: `Error: ${error}`,
            timestamp: new Date(),
          },
        ];
      });
    },
  });
  const isBusy = isExecuting || isAwaitingExecutionStart;

  // Proposal identity/configuration and latest execution are distinct backend records.
  const [abvSessionSnapshot, setAbvSessionSnapshot] = useState<ABVSnapshot>(emptyABVSnapshot(''));
  // Operating selection for the auto-trading creation proof: real account and
  // supported symbol chosen up front; fixed controls are read from the backend
  // operating-profile owner and never restated here.
  const [operatingProfile, setOperatingProfile] = useState<BuilderOperatingProfile | null>(null);
  const [operatingProfileStatus, setOperatingProfileStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [operatingAccounts, setOperatingAccounts] = useState<any[]>([]);
  const [operatingAccountsLoading, setOperatingAccountsLoading] = useState(false);
  const [operatingSelection, setOperatingSelection] = useState<{ accountId: string; symbol: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    setOperatingAccountsLoading(true);
    Promise.all([agentApi.getOperatingProfile().catch(() => null), accountApi.list().catch(() => [])])
      .then(([profile, accounts]) => {
        if (cancelled) return;
        setOperatingProfile(profile as BuilderOperatingProfile | null);
        setOperatingProfileStatus(profile ? 'ready' : 'error');
        setOperatingAccounts(accounts as any[]);
        const preferred = (accounts as any[]).find(item => item.active) ?? (accounts as any[])[0];
        const symbol = profile?.supported_symbols?.[0] ?? null;
        // A default is only appropriate when nothing is saved or explicitly
        // chosen: a late account-list response must never overwrite a hydrated
        // saved selection (or a user choice) with the first active account.
        if (preferred && symbol) setOperatingSelection(prev => prev ?? { accountId: preferred.id, symbol });
      })
      .finally(() => { if (!cancelled) setOperatingAccountsLoading(false); });
    return () => { cancelled = true; };
  }, []);
  // ONE typed wire shape for the operating selection everywhere it is sent.
  const operatingSelectionWire = React.useCallback(
    () => operatingSelection ? { account_id: operatingSelection.accountId, symbol: operatingSelection.symbol } : null,
    [operatingSelection]);
  // The ordinary creation path requires the operating context: a send without a
  // validated selection (or while/loading after a failed profile load) is
  // blocked honestly instead of silently starting a legacy path.
  const ensureOperatingSelection = React.useCallback((): boolean => {
    if (operatingProfileStatus === 'loading') {
      toast.error('Operating controls are still loading; the request was not sent.');
      return false;
    }
    if (operatingProfileStatus === 'error' || !operatingProfile) {
      toast.error('Operating controls are unavailable right now; no request was sent.');
      return false;
    }
    if (!operatingSelection) {
      toast.error('Select an account and symbol before designing the system.');
      return false;
    }
    return true;
  }, [operatingProfile, operatingProfileStatus, operatingSelection]);
  useEffect(() => {
    let cancelled = false;
    setAbvSessionSnapshot(emptyABVSnapshot(sessionId ?? ''));
    setSnapshotLoading(Boolean(sessionId));
    if (!sessionId) return () => { cancelled = true; };
    loadABVSnapshot(agentApi, sessionId, system.id,
      pendingApproval?.sessionId === sessionId ? pendingApproval.executionId : null)
      .then(snapshot => {
        if (cancelled) return;
        setAbvSessionSnapshot(snapshot);
        if (pendingApproval?.sessionId === sessionId && !pendingApproval.executionId && snapshot.reference?.build_execution_id) {
          const id = snapshot.reference.build_execution_id;
          if (snapshot.running) {
            setPendingApproval({ sessionId, executionId: id });
            setApprovalError(null);
            void Promise.resolve().then(() => subscribeToExecution(id)).catch(() => setApprovalError('approval_status_unknown'));
          } else {
            approvalLock.current = false;
            setPendingApproval(null);
            setApprovalError(null);
          }
        }
      })
      .catch(() => { if (!cancelled) setAbvSessionSnapshot({ ...emptyABVSnapshot(sessionId), workflowStatus: 'status_unavailable' }); })
      .finally(() => { if (!cancelled) setSnapshotLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, system.id, abvRefresh, pendingApproval?.executionId, pendingApproval?.sessionId]);
  const snapshotCurrent = abvSessionSnapshot.sessionId === sessionId;
  // Hydrate the selector from the saved draft's backend-bound identity so a
  // reload or session switch displays the selection that is actually saved;
  // the snapshot's own cancelled-guard drops late responses from other sessions.
  useEffect(() => {
    if (!snapshotCurrent) return;
    const saved = abvSessionSnapshot.operatingSelection;
    if (saved && (saved.accountId !== operatingSelection?.accountId || saved.symbol !== operatingSelection?.symbol)) {
      setOperatingSelection(saved);
    }
  }, [snapshotCurrent, abvSessionSnapshot.operatingSelection?.accountId, abvSessionSnapshot.operatingSelection?.symbol]);
  const approvalInFlight = pendingApproval?.sessionId === sessionId;
  useEffect(() => {
    setApprovalError(null);
    if (approvalRequestSession.current !== sessionId) {
      approvalLock.current = false;
      approvalRequestSession.current = null;
      setPendingApproval(null);
    }
  }, [sessionId]);
  // The displayed selection must match the candidate's saved selection: an
  // approval button over an older account's proposal is not offered.
  const selectionMismatch = Boolean(snapshotCurrent && abvSessionSnapshot.operatingSelection && operatingSelection
    && (abvSessionSnapshot.operatingSelection.accountId !== operatingSelection.accountId
        || abvSessionSnapshot.operatingSelection.symbol !== operatingSelection.symbol));
  const abvWorkflowStatus = !snapshotCurrent || snapshotLoading ? null : approvalError ??
    (selectionMismatch ? 'operating_selection_changed'
      : approvalInFlight ? 'installing' : isBusy ? (abvSessionSnapshot.workflowStatus === 'installing' ? 'installing' : 'preparing') : abvSessionSnapshot.workflowStatus);
  const abvProposalRevision = snapshotCurrent ? abvSessionSnapshot.reference?.revision ?? null : null;
  const abvPolicyFacts = snapshotCurrent ? abvSessionSnapshot.policyFacts : null;

  const approveDisplayedProposal = useCallback(async () => {
    const reference = abvSessionSnapshot.reference;
    if (approvalLock.current || isBusy || snapshotLoading || !sessionId ||
        abvSessionSnapshot.sessionId !== sessionId || reference?.target_system_id !== system.id ||
        abvSessionSnapshot.workflowStatus !== 'awaiting_user_approval' || !systemBuilderAccess.ensureAccess()) return;
    approvalLock.current = true;
    approvalRequestSession.current = sessionId;
    setApprovalError(null);
    setPendingApproval({ sessionId, executionId: null });
    try {
      const accepted = await agentApi.execute(agent.id, {
        message: '', session_id: sessionId, ai_system_id: system.id,
        approve_proposal_reference: { execution_id: reference.execution_id, revision: reference.revision,
          fingerprint: reference.fingerprint, target_system_id: reference.target_system_id },
      });
      if (!accepted.id) throw new Error('Approval acknowledgment is missing an execution ID');
      if (sessionIdRef.current !== sessionId) return;
      setPendingApproval({ sessionId, executionId: String(accepted.id) });
      await subscribeToExecution(String(accepted.id));
      refreshABV(value => value + 1);
    } catch (error) {
      // A failed transport may have started work: reconcile before permitting another submission.
      if (sessionIdRef.current === sessionId) {
        const status = (error as any)?.response?.status;
        if (status >= 400 && status < 500 && status !== 408) {
          approvalLock.current = false;
          approvalRequestSession.current = null;
          setPendingApproval(null);
          setApprovalError('approval_rejected');
        } else setApprovalError('approval_status_unknown');
      }
      toast.error(error instanceof Error ? error.message : 'Approval status unavailable');
      refreshABV(value => value + 1);
    }
  }, [abvSessionSnapshot, agent.id, isBusy, sessionId, snapshotLoading, subscribeToExecution, system.id, systemBuilderAccess]);

  // Selector change on a saved draft: rebind through the same revision owner
  // (typed request) so the saved design follows the actual selection and any
  // prepared proposal for the older selection is invalidated. Without a saved
  // draft the local selection simply binds at the next design save.
  const submitSelectionRebind = useCallback(async (selection: { accountId: string; symbol: string }) => {
    if (!sessionId || isBusy || !systemBuilderAccess.ensureAccess()) return;
    try {
      const accepted = await agentApi.execute(agent.id, {
        message: '', session_id: sessionId, ai_system_id: system.id,
        rebind_operating_selection: { account_id: selection.accountId, symbol: selection.symbol },
      });
      if (!accepted?.id) throw new Error('Selection change acknowledgment is missing an execution ID');
      await subscribeToExecution(String(accepted.id));
      refreshABV(value => value + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Selection change failed');
      refreshABV(value => value + 1);
    }
  }, [agent.id, isBusy, refreshABV, sessionId, subscribeToExecution, system.id, systemBuilderAccess]);

  const handleOperatingSelect = useCallback((selection: { accountId: string; symbol: string }) => {
    setOperatingSelection(selection);
    const draftSaved = snapshotCurrent && Boolean(abvSessionSnapshot.reference);
    if (draftSaved && !isBusy) void submitSelectionRebind(selection);
  }, [abvSessionSnapshot.reference, isBusy, snapshotCurrent, submitSelectionRebind]);

  // Direct draft expert edit: typed request through the same saved-revision
  // owner the Architect uses; the backend saves a new revision and invalidates
  // any affected preparation/approval. No model turn is involved.
  const submitDirectExpertEdit = useCallback(async (key: string, prompt: string) => {
    if (!sessionId || isBusy || !prompt.trim() || !systemBuilderAccess.ensureAccess()) return;
    setApprovalError(null);
    try {
      const accepted = await agentApi.execute(agent.id, {
        message: '', session_id: sessionId, ai_system_id: system.id,
        revise_design_blocks: { expert_updates: [{ key, prompt }] },
      });
      if (!accepted?.id) throw new Error('Draft edit acknowledgment is missing an execution ID');
      setAbvSessionSnapshot(prev => ({ ...prev, running: true }));
      await subscribeToExecution(String(accepted.id));
      refreshABV(value => value + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Draft edit failed');
      refreshABV(value => value + 1);
    }
  }, [agent.id, isBusy, refreshABV, sessionId, subscribeToExecution, system.id, systemBuilderAccess]);

  const sendPlainMessage = useCallback(async (text: string) => {
    if (isBusy) return;
    if (!systemBuilderAccess.ensureAccess()) return;
    if (!ensureOperatingSelection()) return;
    setApprovalError(null);
    const messageContent = messages.length === 0
      ? `AI System (name: ${system.name}, ID: ${system.id})

${text}`
      : text;
    setMessages((prev) => [...prev, {
      id: `user-${Date.now()}`, role: 'USER' as const, content: text,
      timestamp: new Date(), hasSnapshot: true, snapshotMode: 'FULL' as const,
    }]);
    try {
      await executeWithMessage(messageContent, sessionId, {
        system_name: system.name,
        force_new_session: userStartedNewConversation.current,
      }, { operating_selection: operatingSelectionWire() });
      userStartedNewConversation.current = false;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send message';
      toast.error(message);
    }
  }, [ensureOperatingSelection, executeWithMessage, isBusy, messages.length, operatingSelectionWire, sessionId, system.id, system.name, systemBuilderAccess]);

  useEffect(() => {
    if (!wsSessionId || sessionId === wsSessionId) {
      return;
    }

    const activeRequestOwnsSession =
      isAwaitingExecutionStart || isExecuting || Boolean(wsExecutionId);
    if (!activeRequestOwnsSession && sessionId) {
      return;
    }

    setSessionId(wsSessionId);
    setIsHistoricalSession(false);
    userStartedNewConversation.current = false;
    queryClient.invalidateQueries({ queryKey: ['conversation-sessions', agent.id, system.id] });
    queryClient.invalidateQueries({ queryKey: ['latest-conversation-session', agent.id, system.id] });
    queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
    queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });
  }, [
    agent.id,
    isAwaitingExecutionStart,
    isExecuting,
    queryClient,
    sessionId,
    system.id,
    wsExecutionId,
    wsSessionId,
  ]);

  const handleVoiceRecording = useCallback(async (file: File) => {
    if (isBusy) return;
    if (!systemBuilderAccess.ensureAccess()) return;
    if (!ensureOperatingSelection()) return;
    if (!isAudioInputSupported(agent.model)) {
      throw new Error(getAudioInputDisabledReason(agent.model));
    }

    try {
      const forceNewSession = userStartedNewConversation.current;
      userStartedNewConversation.current = false;
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording', 'AUDIO');
      const visibleContent = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      let messageContent = visibleContent;
      if (messages.length === 0) {
        messageContent = `AI System (name: ${system.name}, ID: ${system.id})\n\n${messageContent}`;
      }

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'USER',
        content: visibleContent,
        timestamp: new Date(),
        hasSnapshot: true,
        snapshotMode: 'FULL',
      };
      setMessages((prev) => [...prev, userMessage]);
      setSelectedMessageIndex(null);
      const response = await executeWithMessage(messageContent, sessionId, {
        system_name: system.name,
        attachment_ids: [attachment.id],
        force_new_session: forceNewSession,
      }, { operating_selection: operatingSelectionWire() });
      if (response.session_id && !sessionId) {
        setSessionId(response.session_id);
        setIsHistoricalSession(false);
        queryClient.invalidateQueries({ queryKey: ['conversation-sessions', agent.id, system.id] });
        queryClient.invalidateQueries({ queryKey: ['latest-conversation-session', agent.id, system.id] });
        queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
        queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });
      }
      setInputMessage('');
    } catch (error) {
      console.error('Voice execution failed:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    }
  }, [agent.id, ensureOperatingSelection, executeWithMessage, isBusy, messages.length, operatingSelectionWire,
      queryClient, sessionId, system.id, system.name, systemBuilderAccess]);

  // Voice chat — record and send audio to the LLM
  const {
    voiceState, isVoiceOn, interimTranscript,
    toggleVoice, speakResponse, stopVoice,
    voices, selectedVoice, setSelectedVoice,
    voiceRate, setVoiceRate,
    isRecordingSupported, isTTSSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
  });
  const audioInputSupported = isAudioInputSupported(agent.model);
  const audioInputDisabledReason = getAudioInputDisabledReason(agent.model);
  const voiceButtonDisabled = systemBuilderAccess.disabled || !audioInputSupported;
  const voiceButtonDisabledReason = systemBuilderAccess.disabled
    ? systemBuilderAccess.blockedMessage
    : audioInputDisabledReason;

  // Speak AI response when a new assistant message appears
  const prevMessagesLenRef = useRef(0);
  useEffect(() => {
    console.log('[Voice Debug] messages.length:', messages.length, 'prev:', prevMessagesLenRef.current, 'isVoiceOn:', isVoiceOn);
    if (messages.length > prevMessagesLenRef.current) {
      const lastMsg = messages[messages.length - 1];
      console.log('[Voice Debug] New message role:', lastMsg.role, 'content length:', lastMsg.content?.length);
      if (lastMsg.role === 'ASSISTANT' && lastMsg.content && isVoiceOn) {
        console.log('[Voice Debug] Speaking:', lastMsg.content.substring(0, 80));
        speakResponse(stripMarkdown(lastMsg.content));
      }
    }
    prevMessagesLenRef.current = messages.length;
  }, [messages, isVoiceOn, speakResponse]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const applySessionData = useCallback(async (sessionData: ConversationSession) => {
    userStartedNewConversation.current = false;
    setSnapshotView(null);

    // Store full session data including snapshot
    setCurrentSessionData(sessionData);

    // Load messages from session with execution IDs and snapshot info
    let loadedMessages: Message[] = sessionData.messages.map((msg: any) => {
      if (msg.role === 'TOOL_CALL' || msg.role === 'tool_call') {
        return {
          id: String(msg.id) || `tool-${Date.now()}-${Math.random()}`,
          role: 'TOOL_CALL' as const,
          content: '',
          timestamp: new Date(msg.created_at),
          toolName: msg.tool_name,
          toolArgs: msg.tool_input,
          toolResult: msg.tool_output,
          toolSuccess: msg.success,
          toolCallId: msg.tool_call_id || String(msg.id),
          subAgentEvents: Array.isArray(msg.sub_agent_events) ? msg.sub_agent_events : [],
          trace: msg.trace || null,
          traceId: msg.trace_id || msg.trace?.trace_id || null,
          rootTraceId: msg.root_trace_id || msg.trace?.root_trace_id || null,
          parentTraceId: msg.parent_trace_id || msg.trace?.parent_trace_id || null,
        };
      }

      return {
        id: String(msg.id) || `msg-${Date.now()}-${Math.random()}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        executionId: msg.execution || undefined,
        snapshotMode: msg.snapshot_mode,
        hasSnapshot: msg.has_snapshot,
        dbMessageId: Number(msg.id) || 0,
      };
    });

    console.log('[AIBuilder] Loaded session messages with snapshot info:', 
      loadedMessages.map(m => ({ id: m.id, role: m.role, hasSnapshot: m.hasSnapshot, snapshotMode: m.snapshotMode }))
    );

    setMessages(loadedMessages);
    setSessionId(sessionData.id);
    setShowHistory(false);

    const runningExecution = sessionData.executions?.find(
      (exec: any) => exec.status === 'RUNNING'
    );

    if (runningExecution) {
      console.log('🔄 Found running execution, subscribing via WebSocket:', runningExecution.id);

      setSelectedMessageIndex(null);

      await subscribeToExecution(runningExecution.id);
      setIsHistoricalSession(false);
      return;
    }

    setIsHistoricalSession(true);

    let lastAssistantMessageIndex = -1;
    for (let i = loadedMessages.length - 1; i >= 0; i--) {
      if (loadedMessages[i].role === 'ASSISTANT' && loadedMessages[i].executionId) {
        lastAssistantMessageIndex = i;
        break;
      }
    }

    // Hydrate the staged-flow card from the SAME authoritative execution the
    // selected message belongs to: one bounded summary fetch stamps the
    // backend workflow_status onto that message (and only that message), so a
    // reload restores the card without a shadow workflow store. A statusless
    // newer message intentionally leaves the card in the design stage rather
    // than blending an older approval state forward.
    if (lastAssistantMessageIndex !== -1 && loadedMessages[lastAssistantMessageIndex].executionId) {
      try {
        const summary = await agentApi.getExecutionSummary(
          String(loadedMessages[lastAssistantMessageIndex].executionId)
        );
        const status = String(
          summary?.result?.workflow_status
          || summary?.result?.architect_builder_validator_runtime?.status
          || '',
        );
        if (status) {
          const stamped = loadedMessages.slice();
          stamped[lastAssistantMessageIndex] = {
            ...stamped[lastAssistantMessageIndex],
            workflowStatus: status,
          };
          loadedMessages = stamped;
        }
      } catch (hydrateError) {
        console.warn('Failed to hydrate workflow status from execution summary:', hydrateError);
      }
    }

    if (lastAssistantMessageIndex !== -1) {
      setSelectedMessageIndex(lastAssistantMessageIndex);
    } else if (sessionData.executions && sessionData.executions.length > 0) {
      setSelectedMessageIndex(null);
    } else {
      setSelectedMessageIndex(null);
    }
  }, [subscribeToExecution]);

  const {
    data: latestSessionData,
    isLoading: isLoadingLatestSession,
  } = useQuery({
    queryKey: ['latest-conversation-session', agent.id, system.id],
    queryFn: async () => agentApi.getLatestSession(agent.id, true, system.id),
    enabled: Boolean(agent.id && system.id) && shouldAutoLoadLatestSession,
    staleTime: 30_000,
    retry: false,
  });

  // Fetch conversation sessions for this agent and AI system
  const { data: sessionsData, isLoading: isLoadingSessions, isError: isSessionsError } = useQuery({
    queryKey: ['conversation-sessions', agent.id, system.id, sessionsPage],
    queryFn: async () => {
      // Pass AI system ID to API to filter server-side, ordered by last_message_at DESC
      const response = await agentApi.getSessions(agent.id, true, sessionsPage, sessionsPageSize, system.id);
      return response;
    },
    enabled: Boolean(agent.id && system.id && showHistory),
    staleTime: 30_000, // Cache for 30s to avoid re-fetching on every mount
    retry: false, // Don't retry on 404 errors
  });

  const groupedMessages = useMemo(() => groupMessagesForAIBuilder(messages), [messages]);
  const selectedMessage = useMemo(() => {
    if (selectedMessageIndex === null) {
      return null;
    }
    const item = groupedMessages[selectedMessageIndex];
    return item && !('type' in item) ? (item as Message) : null;
  }, [groupedMessages, selectedMessageIndex]);
  const currentExecutionId = wsExecutionId;
  const selectedExecutionId =
    selectedMessage?.executionId ||
    ((currentExecutionId && (!selectedMessage || selectedMessage.executionId === currentExecutionId))
      ? currentExecutionId
      : null);

  const {
    data: selectedExecutionDetail,
    isFetching: isFetchingSelectedExecutionDetail,
  } = useQuery<AgentExecution>({
    queryKey: ['builder-execution-detail', selectedExecutionId],
    queryFn: async () => agentApi.getExecution(selectedExecutionId as string),
    enabled: Boolean(selectedExecutionId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const {
    data: builderIntentLedgerResponse,
    isFetching: isFetchingBuilderIntentLedger,
  } = useQuery<BuilderIntentLedgerResponse>({
    queryKey: ['builder-intent-ledger', system.id, sessionId],
    queryFn: () => agentApi.getBuilderIntentLedger({ sessionId, aiSystemId: system.id }),
    enabled: Boolean(system.id),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const {
    data: activeBuilderArtifactResponse,
    isFetching: isFetchingActiveBuilderArtifact,
  } = useQuery<BuilderArtifactWorkflowResponse>({
    queryKey: ['builder-active-artifact', system.id, sessionId],
    queryFn: () => agentApi.getActiveBuilderArtifact({ sessionId, aiSystemId: system.id }),
    enabled: Boolean(system.id),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const activeBuilderArtifact = activeBuilderArtifactResponse?.artifact ?? null;

  const getActiveBuilderArtifactApprovalReference = useCallback(() => {
    const artifact = activeBuilderArtifactResponse?.artifact;
    return artifact?.approval_reference || artifact?.review_payload?.approval_reference || null;
  }, [activeBuilderArtifactResponse?.artifact]);

  const refreshActiveBuilderArtifact = useCallback(async (artifact?: BuilderArtifactWorkflowResponse['artifact']) => {
    if (artifact) {
      queryClient.setQueryData<BuilderArtifactWorkflowResponse>(['builder-active-artifact', system.id, sessionId], { artifact });
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] }),
      queryClient.invalidateQueries({ queryKey: ['systems'] }),
      queryClient.invalidateQueries({ queryKey: ['system', system.id] }),
    ]);
    await queryClient.refetchQueries({ queryKey: ['builder-active-artifact', system.id] });
  }, [queryClient, sessionId, system.id]);

  const refreshAppliedSystemData = useCallback(async () => {
    if (onExpertCreated) {
      await onExpertCreated();
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ai-system', String(system.id)] }),
      queryClient.invalidateQueries({ queryKey: ['ai-system', system.id] }),
      queryClient.invalidateQueries({ queryKey: ['experts', system.id] }),
      queryClient.invalidateQueries({ queryKey: ['shared-inputs', system.id] }),
      queryClient.invalidateQueries({ queryKey: ['schedules', system.id] }),
      queryClient.invalidateQueries({ queryKey: ['ai-systems'] }),
      queryClient.invalidateQueries({ queryKey: ['systems'] }),
      queryClient.invalidateQueries({ queryKey: ['system', system.id] }),
      queryClient.invalidateQueries({ queryKey: ['experts', system.id, 'canvas-library'] }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['ai-system', String(system.id)] }),
      queryClient.refetchQueries({ queryKey: ['ai-system', system.id] }),
      queryClient.refetchQueries({ queryKey: ['experts', system.id] }),
      queryClient.refetchQueries({ queryKey: ['experts', system.id, 'canvas-library'] }),
      queryClient.refetchQueries({ queryKey: ['shared-inputs', system.id] }),
      queryClient.refetchQueries({ queryKey: ['schedules', system.id] }),
    ]);
  }, [onExpertCreated, queryClient, system.id]);

  const completeBuilderArtifactApply = useCallback(async (
    artifact: NonNullable<BuilderArtifactWorkflowResponse['artifact']>,
    approvalReference: Record<string, any>,
  ) => {
    const response = await agentApi.applyBuilderArtifact({ artifactId: artifact.id, approvalReference });
    await refreshActiveBuilderArtifact(response.artifact);
    await refreshAppliedSystemData();
    queryClient.invalidateQueries({ queryKey: ['latest-conversation-session', agent.id, system.id] });
    queryClient.invalidateQueries({ queryKey: ['conversation-sessions', agent.id, system.id] });
    const applyStatus = typeof response.apply?.summary?.status === 'string'
      ? response.apply.summary.status
      : null;
    if (applyStatus === 'failed') {
      toast.error(isScopedBuilderArtifactUpdate(artifact) ? 'Update apply failed' : 'Plan apply failed');
    } else if (isScopedBuilderArtifactUpdate(artifact)) {
      toast.success(applyStatus === 'applied' ? 'Update applied and verified' : 'Update apply completed');
    } else {
      toast.success(applyStatus === 'applied' ? 'Plan approved and built' : 'Plan approval and build completed');
    }
    return response;
  }, [agent.id, queryClient, refreshActiveBuilderArtifact, refreshAppliedSystemData, system.id]);

  const handleApproveActiveBuilderArtifact = useCallback(async () => {
    const artifact = activeBuilderArtifactResponse?.artifact;
    const approvalReference = getActiveBuilderArtifactApprovalReference();
    if (!artifact || !approvalReference) {
      toast.error('No current plan approval reference is available');
      return;
    }
    setBuilderArtifactAction('approve');
    try {
      const response = await agentApi.approveBuilderArtifact({ artifactId: artifact.id, approvalReference });
      const approvedArtifact = response.artifact;
      const approvedReference = approvedArtifact?.approval_reference || approvedArtifact?.review_payload?.approval_reference;
      if (!approvedArtifact || !approvedReference) {
        await refreshActiveBuilderArtifact(approvedArtifact);
        toast.error('Plan was approved but could not start the build');
        return;
      }
      await completeBuilderArtifactApply(approvedArtifact, approvedReference);
    } finally {
      setBuilderArtifactAction(null);
    }
  }, [activeBuilderArtifactResponse?.artifact, completeBuilderArtifactApply, getActiveBuilderArtifactApprovalReference, refreshActiveBuilderArtifact]);

  const handleApplyActiveBuilderArtifact = useCallback(async () => {
    const artifact = activeBuilderArtifactResponse?.artifact;
    const approvalReference = getActiveBuilderArtifactApprovalReference();
    if (!artifact || !approvalReference) {
      toast.error('No current plan approval reference is available');
      return;
    }
    setBuilderArtifactAction('apply');
    try {
      await completeBuilderArtifactApply(artifact, approvalReference);
    } finally {
      setBuilderArtifactAction(null);
    }
  }, [activeBuilderArtifactResponse?.artifact, completeBuilderArtifactApply, getActiveBuilderArtifactApprovalReference]);

  const handleUpdateBuilderIntentItem = useCallback(async (
    ledgerId: string,
    itemId: string,
    patch: BuilderIntentItemPatchRequest,
  ) => {
    setUpdatingBuilderIntentItemId(itemId);
    try {
      const response = await agentApi.updateBuilderIntentItem({ ledgerId, itemId, patch });
      queryClient.setQueryData<BuilderIntentLedgerResponse>(['builder-intent-ledger', system.id, sessionId], response);
      queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
      queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });
      setBuilderIntentLedgerNotice(null);
      toast.success('Intent updated');
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.ledger) {
        queryClient.setQueryData<BuilderIntentLedgerResponse>(['builder-intent-ledger', system.id, sessionId], { ledger: responseData.ledger });
      }
      const detail = responseData?.detail;
      const message = responseData?.error === 'ledger_version_conflict'
        ? 'Intent ledger changed. Review the latest version and try again.'
        : Array.isArray(detail)
          ? detail.join(' ')
          : typeof detail === 'string'
            ? detail
            : 'Failed to update intent item';
          if (responseData?.error === 'ledger_version_conflict') {
            setBuilderIntentLedgerNotice(message);
          }
      toast.error(message);
      throw error;
    } finally {
      setUpdatingBuilderIntentItemId(null);
    }
  }, [queryClient, sessionId, system.id]);

  const handleAppendBuilderIntentLedgerEvent = useCallback(async (
    ledgerId: string,
    event: BuilderIntentLedgerEventRequest,
    actionItemId?: string,
  ) => {
    setUpdatingBuilderIntentItemId(actionItemId ?? ledgerId);
    try {
      const response = await agentApi.appendBuilderIntentLedgerEvent({ ledgerId, event });
      queryClient.setQueryData<BuilderIntentLedgerResponse>(['builder-intent-ledger', system.id, sessionId], response);
      queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
      queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });
      setBuilderIntentLedgerNotice(null);
      toast.success('Intent ledger updated');
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.ledger) {
        queryClient.setQueryData<BuilderIntentLedgerResponse>(['builder-intent-ledger', system.id, sessionId], { ledger: responseData.ledger });
      }
      const detail = responseData?.detail;
      const message = responseData?.error === 'ledger_version_conflict'
        ? 'Intent ledger changed. Review the latest version and try again.'
        : Array.isArray(detail)
          ? detail.join(' ')
          : typeof detail === 'string'
            ? detail
            : 'Failed to update intent ledger';
          if (responseData?.error === 'ledger_version_conflict') {
            setBuilderIntentLedgerNotice(message);
          }
      toast.error(message);
      throw error;
    } finally {
      setUpdatingBuilderIntentItemId(null);
    }
  }, [queryClient, sessionId, system.id]);

  const handleSelectLedgerSourceMessage = useCallback((sourceMessageId: string) => {
    const sourceKey = String(sourceMessageId);
    const messageIndex = groupedMessages.findIndex((item) => {
      if ('type' in item) {
        return false;
      }
      const message = item as Message;
      return message.id === sourceKey || String(message.dbMessageId || '') === sourceKey;
    });

    if (messageIndex === -1) {
      toast.info('Source message is not in the loaded conversation');
      return;
    }

    const message = groupedMessages[messageIndex] as Message;
    const target = messageRefs.current[message.id] || messageRefs.current[sourceKey];
    setSelectedMessageIndex(messageIndex);
    setSourceHighlightedMessageId(message.id);
    if (isMobile) {
      setMobileTab(0);
    }
    window.setTimeout(() => {
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    window.setTimeout(() => {
      setSourceHighlightedMessageId((current) => current === message.id ? null : current);
    }, 2400);
  }, [groupedMessages, isMobile]);

  const builderStateForStatus = useMemo(() => {
    const detailState = extractBuilderStateFromExecutionDetail(selectedExecutionDetail);
    if (detailState) {
      return detailState;
    }
    if (
      currentExecutionId &&
      (!selectedExecutionId || selectedExecutionId === currentExecutionId)
    ) {
      return extractBuilderStateFromExecutionPlan(currentExecutionPlan);
    }
    return null;
  }, [currentExecutionId, currentExecutionPlan, selectedExecutionDetail, selectedExecutionId]);
  const builderPlanPayload = useMemo(() => {
    const detailPayload = extractBuilderPlanPayloadFromExecutionDetail(selectedExecutionDetail);
    if (detailPayload) {
      return detailPayload;
    }
    if (
      currentExecutionId &&
      (!selectedExecutionId || selectedExecutionId === currentExecutionId)
    ) {
      return extractBuilderPlanPayloadFromExecutionPlan(currentExecutionPlan);
    }
    return null;
  }, [currentExecutionId, currentExecutionPlan, selectedExecutionDetail, selectedExecutionId]);

  const builderPlanStatusView = useMemo(
    () =>
      buildBuilderPlanStatusView(builderStateForStatus, {
        fallbackSystemId: system.id,
        fallbackSystemName: system.name,
      }),
    [builderStateForStatus, system.id, system.name]
  );
  const builderPlanDetailView = useMemo(
    () =>
      buildBuilderPlanDetailView(builderStateForStatus, {
        fallbackSystemId: system.id,
        fallbackSystemName: system.name,
      }),
    [builderStateForStatus, system.id, system.name]
  );
  const builderPlanReviewView = useMemo(
    () => buildBuilderPlanReviewView(builderStateForStatus),
    [builderStateForStatus]
  );
  const builderPlanPreviewView = useMemo(
    () => buildBuilderPlanPreviewView(builderPlanPayload, builderStateForStatus),
    [builderPlanPayload, builderStateForStatus]
  );
  const builderPlanHealthView = useMemo(
    () => buildBuilderPlanHealthView(builderPlanPayload, builderStateForStatus),
    [builderPlanPayload, builderStateForStatus]
  );
  const builderPlanDiffView = useMemo(
    () => buildBuilderPlanDiffView(builderPlanPayload),
    [builderPlanPayload]
  );
  const builderPlanRevisionView = useMemo(
    () => buildBuilderPlanRevisionView(activeBuilderArtifact) ?? buildBuilderPlanRevisionView(builderPlanPayload),
    [activeBuilderArtifact, builderPlanPayload]
  );
  const builderPlanActionView = useMemo(
    () => buildBuilderPlanActionView(builderStateForStatus, builderPlanPayload, {
      fallbackSystemId: system.id,
      fallbackSystemName: system.name,
    }),
    [builderPlanPayload, builderStateForStatus, system.id, system.name]
  );

  const handleUseSuggestedReply = useCallback((message: string) => {
    setInputMessage(message);
    if (isMobile) {
      setMobileTab(0);
    }
    window.setTimeout(() => {
      const input = chatInputRef.current;
      input?.focus();
      if (input && 'selectionStart' in input && 'selectionEnd' in input) {
        try {
          input.selectionStart = message.length;
          input.selectionEnd = message.length;
        } catch {
          // Ignore selection APIs that are unsupported for the mounted input element.
        }
      }
    }, 0);
  }, [isMobile]);

  // Reset to page 1 if we get an error (e.g., page out of range)
  useEffect(() => {
    if (isSessionsError && sessionsPage > 1) {
      setSessionsPage(1);
    }
  }, [isSessionsError, sessionsPage]);

  // Reset to previous page if current page is empty but there are sessions
  useEffect(() => {
    if (sessionsData && sessionsData.results.length === 0 && sessionsData.count > 0 && sessionsPage > 1) {
      // Current page is empty, go to previous page
      setSessionsPage(sessionsPage - 1);
    }
  }, [sessionsData, sessionsPage]);

  // Auto-load the latest session detail without waiting on history pagination.
  useEffect(() => {
    if (!latestSessionData || !shouldAutoLoadLatestSession) {
      return;
    }

    let shouldLogErrors = true;

    const loadLatestSession = async () => {
      setIsLoadingSession(true);
      try {
        await applySessionData(latestSessionData);
      } catch (error) {
        if (shouldLogErrors) {
          console.error('Failed to load latest conversation:', error);
        }
      } finally {
        setIsLoadingSession(false);
      }
    };

    void loadLatestSession();

    return () => {
      shouldLogErrors = false;
    };
  }, [applySessionData, latestSessionData, shouldAutoLoadLatestSession]);

  // Handle stop execution via WebSocket hook
  const handleStopExecution = async () => {
    if (currentExecutionId) {
      // Add immediate feedback
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-stopping-${Date.now()}`,
          role: 'ASSISTANT' as const,
          content: '⚠️ Stopping execution...',
          timestamp: new Date(),
        },
      ]);
      await stopExecution();
    }
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!systemBuilderAccess.ensureAccess()) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        console.log('[AIBuilder] Uploading file:', file.name, 'size:', file.size, 'type:', file.type);
        const attachment = await agentApi.uploadAttachment(file);
        console.log('[AIBuilder] Upload response - ID:', attachment.id, 'name:', attachment.original_name);
        return attachment;
      });

      const uploadedAttachments = await Promise.all(uploadPromises);
      setPendingAttachments((prev) => {
        const newList = [...prev, ...uploadedAttachments];
        console.log('[AIBuilder] Updated pendingAttachments:', newList.map(a => ({ id: a.id, name: a.original_name })));
        return newList;
      });
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setIsUploading(false);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove a pending attachment
  const handleRemoveAttachment = async (attachmentId: string) => {
    try {
      await agentApi.deleteAttachment(attachmentId);
      setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (error) {
      console.error('Failed to remove attachment:', error);
    }
  };

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && pendingAttachments.length === 0) || isBusy) return;
    if (!systemBuilderAccess.ensureAccess()) return;

    const forceNewSession = userStartedNewConversation.current;
    userStartedNewConversation.current = false;

    // Prepare message content - keep system info hidden from the visible chat bubble.
    let visibleContent = inputMessage;
    let messageContent = inputMessage;
    if (messages.length === 0) {
      messageContent = `AI System (name: ${system.name}, ID: ${system.id})\n\n${inputMessage}`;
    }

    // Add file information to the message if there are attachments
    if (pendingAttachments.length > 0) {
      const fileList = pendingAttachments
        .map((a) => `- ${a.original_name} (${formatFileSize(a.size)}, ${a.attachment_type})${a.file_url ? ` [View](${a.file_url})` : ''}`)
        .join('\n');
      visibleContent = visibleContent
        ? `${visibleContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
      messageContent = messageContent 
        ? `${messageContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
    }

    // Add user message with snapshot info (all new messages get snapshots)
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'USER',
      content: visibleContent,
      timestamp: new Date(),
      hasSnapshot: true, // New messages always get snapshots
      snapshotMode: 'FULL',
    };
    setMessages((prev) => [...prev, userMessage]);
    
    // Clear selection for new execution
    setSelectedMessageIndex(null);
    
    // Clear pending attachments after including them in the message
    const attachmentsToSend = pendingAttachments;
    const attachmentIds = attachmentsToSend.map((a) => a.id);
    setPendingAttachments([]);
    
    // Execute agent via WebSocket-enabled hook
    try {
      if (!ensureOperatingSelection()) { setPendingAttachments(attachmentsToSend); return; }
      const response = await executeWithMessage(messageContent, sessionId, {
        system_name: system.name,
        attachment_ids: attachmentIds,
        force_new_session: forceNewSession,
      }, { operating_selection: operatingSelectionWire() });
      
      // Update session ID if this is a new session
      if (response.session_id && !sessionId) {
        console.log('📝 New session created:', response.session_id, '- updating state and refetching sessions');
        setSessionId(response.session_id);
        setIsHistoricalSession(false);
        // Also invalidate to ensure fresh fetch
        queryClient.invalidateQueries({ 
          queryKey: ['conversation-sessions', agent.id, system.id]
        });
        queryClient.invalidateQueries({
          queryKey: ['latest-conversation-session', agent.id, system.id]
        });
        queryClient.invalidateQueries({ queryKey: ['builder-intent-ledger', system.id] });
        queryClient.invalidateQueries({ queryKey: ['builder-active-artifact', system.id] });
      }

      setInputMessage('');
    } catch (error) {
      console.error('Execution failed:', error);
      const message = error instanceof Error ? error.message : 'Failed to send message';
      toast.error(message);
      setPendingAttachments(attachmentsToSend);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLoadSession = async (selectedSessionId: string) => {
    setIsLoadingSession(true);
    try {
      const sessionData = await agentApi.getSession(selectedSessionId);
      await applySessionData(sessionData);
    } catch (error) {
      console.error('Failed to load session:', error);
    } finally {
      setIsLoadingSession(false);
    }
  };

  const handleNewConversation = () => {
    // Check if current session is already empty (no messages) - don't create another
    if (messages.length === 0 && sessionId === null) {
      console.log('handleNewConversation - already in a fresh state, skipping');
      return;
    }
    
    console.log('Starting new conversation - setting userStartedNewConversation flag');
    userStartedNewConversation.current = true; // Prevent auto-loading
    pendingSubAgentEventsRef.current = {};
    setMessages([]);
    setSessionId(null);
    setCurrentSessionData(null); // Clear session data
    setSnapshotView(null);
    setIsHistoricalSession(false); // Not a historical session
    setSelectedMessageIndex(null); // Clear selection
    setShowHistory(false);
    setPendingAttachments([]); // Clear any pending attachments
    resetExecution(); // Reset WebSocket execution state
  };

  // Handle restore from checkpoint
  const handleRestoreClick = (message: Message, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent message click
    setRestoreMessage(message);
    setRestoreDialogOpen(true);
  };

  const handleViewSnapshotClick = async (message: Message, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!sessionId || !message.dbMessageId) {
      setRestoreSnackbar({
        open: true,
        message: 'Cannot view snapshot: missing session or message data',
        severity: 'error',
      });
      return;
    }

    setSnapshotLoadingMessageId(message.id);
    try {
      const result = await agentApi.getMessageSnapshot(sessionId, message.dbMessageId);
      if (!result.has_snapshot || !result.system_snapshot) {
        setRestoreSnackbar({
          open: true,
          message: 'No snapshot is available for this checkpoint',
          severity: 'error',
        });
        return;
      }

      setSnapshotView({ messageId: message.id, snapshot: result.system_snapshot });
      if (isMobile) {
        setMobileTab(1);
      }
    } catch (error: any) {
      console.error('Failed to load message snapshot:', error);
      setRestoreSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load checkpoint snapshot',
        severity: 'error',
      });
    } finally {
      setSnapshotLoadingMessageId(null);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoreMessage || !sessionId || !restoreMessage.dbMessageId) {
      setRestoreSnackbar({
        open: true,
        message: 'Cannot restore: missing session or message data',
        severity: 'error',
      });
      setRestoreDialogOpen(false);
      return;
    }

    setIsRestoring(true);
    try {
      const options: RestoreSnapshotOptions = {
        restore_experts: true,
        restore_schedules: true,
        restore_system_config: true,
        delete_extra: true, // Full rollback: remove items added after checkpoint
      };

      const result = await agentApi.restoreFromSnapshot(
        sessionId,
        restoreMessage.dbMessageId,
        options
      );

      if (result.status === 'success') {
        setRestoreSnackbar({
          open: true,
          message: `System restored to checkpoint! ${result.restoration_log.experts_updated.length} experts updated.`,
          severity: 'success',
        });

        // Refresh the system data
        if (onExpertCreated) {
          await onExpertCreated();
        }
        queryClient.invalidateQueries({ queryKey: ['ai-system', String(system.id)] });
        queryClient.invalidateQueries({ queryKey: ['experts', system.id] });
        queryClient.invalidateQueries({ queryKey: ['shared-inputs', system.id] });
        queryClient.invalidateQueries({ queryKey: ['schedules', system.id] });
      } else {
        setRestoreSnackbar({
          open: true,
          message: result.message || 'Restoration failed',
          severity: 'error',
        });
      }
    } catch (error: any) {
      console.error('Failed to restore from snapshot:', error);
      setRestoreSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to restore from checkpoint',
        severity: 'error',
      });
    } finally {
      setIsRestoring(false);
      setRestoreDialogOpen(false);
      setRestoreMessage(null);
    }
  };

  const handleRestoreCancel = () => {
    setRestoreDialogOpen(false);
    setRestoreMessage(null);
  };

  // Handle clicking on a message to view its execution data
  const handleMessageClick = (index: number, message: Message) => {
    if (message.role === 'ASSISTANT' && message.executionId) {
      setSelectedMessageIndex(index);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, overflow: 'hidden', width: '100%' }}>
      {/* History Drawer - Render outside main container */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={showHistory}
        onClose={() => {
          console.log('Drawer onClose called');
          setShowHistory(false);
        }}
        sx={{
          '& .MuiDrawer-root': {
            position: 'fixed',
          },
          '& .MuiModal-root': {
            position: 'fixed',
            zIndex: 1300,
          },
        }}
        PaperProps={{
          sx: {
            width: 320,
            bgcolor: 'background.paper',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1300,
          }
        }}
        ModalProps={{
          keepMounted: false,
          style: {
            position: 'fixed',
            zIndex: 1300,
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.paper', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Conversations
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              aria-label="New conversation"
                      onClick={handleNewConversation}
              variant="outlined"
            >
              New
            </Button>
          </Box>

          {isLoadingSessions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Sessions List */}
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {sessionsData && sessionsData.results && sessionsData.results.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {sessionsData.results.map((session) => (
                      <ListItem key={session.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                          onClick={() => handleLoadSession(session.id)}
                          selected={sessionId === session.id}
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: sessionId === session.id ? 'primary.main' : 'divider',
                            '&.Mui-selected': {
                              bgcolor: isDark ? 'primary.900' : 'primary.50',
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Badge badgeContent={session.message_count} color="primary">
                              <ChatBubbleOutlineIcon />
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              cleanMarkdownTitle(
                                session.title || 
                                session.first_message_preview ||
                                (session.last_message_preview?.content 
                                  ? session.last_message_preview.content.substring(0, 30) + '...'
                                  : 'Untitled conversation')
                              )
                            }
                            secondary={
                              session.last_message_at
                                ? formatStoredDateTime(session.last_message_at, undefined, '-')
                                : formatStoredDateTime(session.created_at, undefined, '-')
                            }
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: sessionId === session.id ? 'bold' : 'normal',
                              noWrap: true,
                            }}
                            secondaryTypographyProps={{
                              variant: 'caption',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : sessionsData && sessionsData.count > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No sessions on this page
                    </Typography>
                    <Button size="small" onClick={() => setSessionsPage(1)}>
                      Go to first page
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No previous conversations
                  </Alert>
                )}
              </Box>
              
              {/* Pagination - calculate actual page count based on results */}
              {sessionsData && (() => {
                // Calculate effective page count:
                // If current page has items but less than pageSize, it's the last page
                const resultsOnPage = sessionsData.results?.length || 0;
                const isLastPageWithContent = resultsOnPage > 0 && resultsOnPage < sessionsPageSize;
                const effectivePageCount = isLastPageWithContent 
                  ? sessionsPage  // Current page is the last with content
                  : Math.ceil(sessionsData.count / sessionsPageSize);
                
                return effectivePageCount > 1 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
                    <Pagination
                      count={effectivePageCount}
                      page={sessionsPage}
                      onChange={(_, page) => setSessionsPage(page)}
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                ) : null;
              })()}
            </>
          )}
        </Box>
      </Drawer>

      {/* Mobile Tab Navigation */}
      {isMobile && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
          <Tabs 
            value={mobileTab} 
            onChange={(_, newValue) => setMobileTab(newValue)}
            variant="fullWidth"
            sx={{ minHeight: 36 }}
          >
            <Tab 
              icon={<SmartToyIcon sx={{ fontSize: 16 }} />} 
              label="Chat" 
              iconPosition="start"
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}
            />
            <Tab 
              icon={<HistoryIcon sx={{ fontSize: 16 }} />} 
              label="Insights" 
              iconPosition="start"
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}
            />
          </Tabs>
        </Box>
      )}

      {/* Main Content - Resizable Split Pane on Desktop, Tabbed on Mobile */}
      <Box ref={containerRef} sx={{ 
        position: 'relative', 
        flex: 1, 
        minHeight: 0, 
        display: 'flex', 
        overflow: 'hidden',
        flexDirection: isMobile ? 'column' : 'row',
        width: '100%',
        maxWidth: '100%',
      }}>
        {/* Left Side: Chat Interface */}
        <Box 
          sx={{ 
            width: isMobile ? '100%' : `${leftPanelWidth}%`, 
            minWidth: isMobile ? 'auto' : 300,
            height: '100%',
            overflow: 'hidden',
            flexShrink: 0,
            display: isMobile ? (mobileTab === 0 ? 'flex' : 'none') : 'flex',
          }}
        >
            <Paper elevation={0} sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', borderRadius: 0 }}>
              <ABVOperatingControls
                accounts={operatingAccounts}
                accountsLoading={operatingAccountsLoading}
                profile={operatingProfile}
                accountId={operatingSelection?.accountId ?? null}
                symbol={operatingSelection?.symbol ?? null}
                onSelect={handleOperatingSelect}
                disabled={isBusy}
              />
              {/* Chat Header - Fixed at top */}
              <Box sx={{ px: { xs: 1, sm: 1.5 }, py: { xs: 0.5, sm: 0.75 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 0.5, sm: 1 } }}>
                  <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon sx={{ fontSize: { xs: 16, sm: 20 }, flexShrink: 0 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Architect
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                      {isSmallMobile ? 'Design chat' : 'Single conversation with background build orchestration'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => setShowHistory(!showHistory)}
                      color={showHistory ? "primary" : "default"}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                    >
                      <HistoryIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="New conversation"
                      onClick={handleNewConversation}
                      disabled={messages.length === 0}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                    >
                      <AddIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {(builderPlanStatusView || isFetchingSelectedExecutionDetail) && (
                <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: { xs: 0.75, sm: 1 }, pb: 0, flexShrink: 0 }}>
                  {isFetchingSelectedExecutionDetail && !builderPlanStatusView ? (
                    <LinearProgress
                      sx={{
                        mb: 1,
                        borderRadius: 999,
                        height: 6,
                      }}
                    />
                  ) : null}
                  {builderPlanStatusView ? (
                    <Alert
                      severity={builderPlanStatusView.tone}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        alignItems: 'flex-start',
                        '& .MuiAlert-message': {
                          width: '100%',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {builderPlanStatusView.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {builderPlanStatusView.body}
                      </Typography>
                      {builderPlanStatusView.chips.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {builderPlanStatusView.chips.map((chip) => (
                            <Chip key={chip} label={chip} size="small" variant="outlined" />
                          ))}
                        </Box>
                      ) : null}
                    </Alert>
                  ) : null}
                </Box>
              )}

              {/* Messages Area - Independently scrollable */}
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                p: { xs: 1, sm: 2 },
                minHeight: 0, // Important for flex scrolling
              }}>
                {/* ABV staged flow: backend-status projection, one ordinary action per boundary */}
                <ABVWorkflowStages
                  workflowStatus={abvWorkflowStatus}
                  proposalRevision={abvProposalRevision}
                  blockedStatus={
                    ['translation_blocked', 'build_preparation_blocked', 'needs_design_repair',
                     'candidate_review_changes_required', 'execution_failed', 'execution_cancelled', 'status_unavailable', 'approval_status_unknown', 'approval_rejected',
                     'proposal_revision_changed', 'proposal_decision_invalid', 'operating_selection_changed'].includes(abvWorkflowStatus ?? '')
                      ? abvWorkflowStatus
                      : null
                  }
                  busy={isBusy || approvalInFlight || snapshotLoading || !snapshotCurrent || abvSessionSnapshot.running}
                  policyFacts={abvPolicyFacts}
                  onPrepareBuild={() => sendPlainMessage('The design is agreed. Please prepare it for my approval.')}
                  onApproveBuild={approveDisplayedProposal}
                  onRefresh={() => { if (!approvalLock.current) setApprovalError(null); refreshABV(value => value + 1); }}
                  onCancel={(isBusy || abvSessionSnapshot.running) ? () => { void stopExecution(); } : undefined}
                  methods={snapshotCurrent ? abvSessionSnapshot.methods : []}
                  operatingMode={operatingProfile?.mode === 'auto_trading' ? 'Auto trading' : null}
                  onEditExpert={submitDirectExpertEdit}
                />
                {(isLoadingSession || (shouldAutoLoadLatestSession && isLoadingLatestSession)) ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Loading conversation...
                    </Typography>
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Start a conversation to design your AI system
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Try: "Design a London breakout system for XAUUSD" or "I want strict risk controls and one execution expert"
                    </Typography>
                  </Box>
                ) : (
                  groupedMessages.map((item, index) => {
                    if ('type' in item && item.type === 'tool_group') {
                      return <ToolCallGroup key={item.id} toolMsgs={item.msgs} isDark={isDark} isStaff={isStaff} />;
                    }
                    const message = item as Message;
                    return (
                    <Box
                      key={message.id}
                      ref={(element: HTMLDivElement | null) => {
                        messageRefs.current[message.id] = element;
                        if (message.dbMessageId) {
                          messageRefs.current[String(message.dbMessageId)] = element;
                        }
                      }}
                      onClick={() => handleMessageClick(index, message)}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        display: 'flex',
                        gap: { xs: 0.75, sm: 1 },
                        alignItems: 'flex-start',
                        cursor: message.role === 'ASSISTANT' && message.executionId ? 'pointer' : 'default',
                        position: 'relative',
                        minWidth: 0,
                        overflow: 'hidden',
                        outline: sourceHighlightedMessageId === message.id ? '2px solid' : 'none',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                        borderRadius: 1.5,
                        '&:hover': message.role === 'ASSISTANT' && message.executionId ? {
                          '& .message-paper': {
                            boxShadow: 2,
                          }
                        } : {},
                      }}
                    >
                      {/* Avatar */}
                      <Box
                        sx={{
                          width: { xs: 28, sm: 32 },
                          height: { xs: 28, sm: 32 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: message.role === 'USER' ? 'primary.main' : 'secondary.main',
                          color: 'white',
                          flexShrink: 0,
                          border: selectedMessageIndex === index && message.role === 'ASSISTANT' ? '2px solid' : 'none',
                          borderColor: 'primary.main',
                        }}
                      >
                        {message.role === 'USER' ? <PersonIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> : <SmartToyIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                          </Box>

                      {/* Message Content */}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" fontWeight="bold">
                            {message.role === 'USER' ? 'You' : 'Architect'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatStoredTime(message.timestamp, undefined, '')}
                          </Typography>
                          {selectedMessageIndex === index && message.role === 'ASSISTANT' && (
                            <Chip 
                              label="Viewing" 
                              size="small" 
                              color="primary" 
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                          {/* Checkpoint indicator and restore button for USER messages with snapshots */}
                          {message.role === 'USER' && message.hasSnapshot && (
                            <>
                              <Tooltip title={`Checkpoint (${message.snapshotMode})`}>
                                <Chip 
                                  icon={<CheckpointIcon sx={{ fontSize: '0.85rem !important' }} />}
                                  label="Checkpoint" 
                                  size="small" 
                                  color="success"
                                  sx={{ height: 20, fontSize: '0.65rem', ml: 1, bgcolor: 'success.main', color: 'white', '& .MuiChip-icon': { ml: 0.5, color: 'white' } }}
                                />
                              </Tooltip>
                              <Tooltip title="View checkpoint snapshot">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleViewSnapshotClick(message, e)}
                                  disabled={snapshotLoadingMessageId === message.id}
                                  sx={{
                                    p: 0.25,
                                    ml: 0.5,
                                    '&:hover': {
                                      bgcolor: 'primary.main',
                                      color: 'primary.contrastText',
                                    }
                                  }}
                                >
                                  {snapshotLoadingMessageId === message.id ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <VisibilityIcon sx={{ fontSize: 14 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Restore to checkpoint">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRestoreClick(message, e)}
                                  sx={{
                                    p: 0.25,
                                    ml: 0.25,
                                    '&:hover': {
                                      bgcolor: 'warning.main',
                                      color: 'warning.contrastText',
                                    }
                                  }}
                                >
                                  <RestoreIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                        <Paper
                          className="message-paper"
                          sx={{
                            p: { xs: 1, sm: 1.5 },
                            bgcolor: message.role === 'USER' 
                              ? (isDark ? 'primary.900' : 'primary.50') 
                              : (isDark ? 'grey.800' : 'grey.50'),
                            border: selectedMessageIndex === index && message.role === 'ASSISTANT' ? '2px solid' : 'none',
                            borderColor: 'primary.main',
                            transition: 'all 0.2s',
                            overflow: 'hidden',
                            maxWidth: '100%',
                          }}
                            >
                          <Box
                            sx={{
                              overflow: 'hidden',
                              maxWidth: '100%',
                              '& p': { margin: '0.5em 0', wordBreak: 'break-word' },
                              '& pre': { 
                                backgroundColor: isDark ? '#2d2d2d' : '#f5f5f5',
                                padding: '12px',
                                borderRadius: '4px',
                                overflow: 'auto',
                                fontSize: '0.85em',
                                maxWidth: '100%',
                                whiteSpace: 'pre',
                              },
                              '& code': {
                                backgroundColor: isDark ? '#2d2d2d' : '#f5f5f5',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '0.9em',
                                fontFamily: 'monospace',
                                wordBreak: 'break-word',
                              },
                              '& pre code': {
                                backgroundColor: 'transparent',
                                padding: 0,
                                wordBreak: 'normal',
                              },
                              '& ul, & ol': {
                                paddingLeft: '1.5em',
                                margin: '0.5em 0',
                              },
                              '& h1, & h2, & h3, & h4, & h5, & h6': {
                                margin: '0.75em 0 0.5em',
                                fontWeight: 600,
                              },
                              '& blockquote': {
                                borderLeft: '3px solid #ddd',
                                paddingLeft: '1em',
                                margin: '0.5em 0',
                                color: 'text.secondary',
                              },
                              '& table': {
                                borderCollapse: 'collapse',
                                width: '100%',
                                margin: '0.5em 0',
                              },
                              '& th, & td': {
                                border: '1px solid',
                                borderColor: isDark ? 'grey.700' : '#ddd',
                                padding: '8px',
                                textAlign: 'left',
                              },
                              '& th': {
                                backgroundColor: isDark ? 'grey.800' : '#f5f5f5',
                                fontWeight: 600,
                              },
                            }}
                          >
                            {message.role === 'USER' ? (
                              <AttachmentMessage content={message.content} />
                            ) : (
                              <BuilderPlanMessage content={message.content} isDark={isDark} />
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                    );
                  })
                    )}

                {/* Tool Confirmation Dialog - shown inline in chat when a dangerous tool requires confirmation */}
                {pendingConfirmation && (
                  <Box sx={{ mb: 2 }}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 2,
                        border: '2px solid',
                        borderColor: 'warning.main',
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 152, 0, 0.04)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                        <Typography variant="subtitle1" fontWeight={600} color="warning.dark">
                          Confirmation Required
                        </Typography>
                        <Chip
                          label={`Danger Level: ${pendingConfirmation.dangerLevel}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        The tool <strong>{pendingConfirmation.toolName}</strong> requires your confirmation before execution.
                      </Typography>
                      {Object.keys(pendingConfirmation.toolArgs).length > 0 && (
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'action.hover',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            overflow: 'auto',
                            maxHeight: 120,
                          }}
                        >
                          {JSON.stringify(pendingConfirmation.toolArgs, null, 2)}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          onClick={() => {
                            confirmTool(
                              pendingConfirmation.executionId,
                              pendingConfirmation.toolName,
                              false
                            );
                          }}
                        >
                          Deny
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          onClick={() => {
                            confirmTool(
                              pendingConfirmation.executionId,
                              pendingConfirmation.toolName,
                              true
                            );
                          }}
                        >
                          Confirm Execution
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Live builder artifact lifecycle (backend-authored statuses only) */}
                {isBusy && lifecycleProgress ? (
                  <BuilderLifecycleCard
                    lifecycle={lifecycleProgress}
                    lastIssue={lastIssue}
                    assistantActivity={assistantActivity}
                    isAwaitingExecutionStart={isAwaitingExecutionStart}
                    wsConnected={wsConnected}
                    isStaff={isStaff}
                    currentExecutionId={currentExecutionId}
                    currentIteration={currentIteration}
                    maxIterations={maxIterations}
                    totalTokens={totalTokens}
                    totalCost={totalCost}
                    isDark={isDark}
                  />
                ) : isBusy && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CircularProgress size={20} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {assistantActivity?.message || (isAwaitingExecutionStart ? 'Preparing your request' : 'Architect is working...')}
                        </Typography>
                        {(assistantActivity?.detail || isAwaitingExecutionStart) && (
                          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
                            {assistantActivity?.detail || 'Running the background build steps.'}
                          </Typography>
                        )}
                        <Typography
                          data-testid="lifecycle-waiting-hint"
                          variant="caption"
                          color="text.secondary"
                          sx={{ opacity: 0.7, display: 'block' }}
                        >
                          Waiting for backend lifecycle events.
                        </Typography>
                      </Box>
                      {/* WebSocket connection indicator */}
                      {wsConnected ? (
                        <WifiIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <WifiOffIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      )}
                    </Box>
                    {/* Expert progress */}
                      {isExecuting && expertProgress.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {expertProgress.map((expert) => (
                          <Chip
                            key={expert.expert_id}
                            label={expert.expert_name}
                            size="small"
                            color={
                              expert.status === 'completed' ? 'success' :
                              expert.status === 'running' ? 'primary' :
                              expert.status === 'error' ? 'error' : 'default'
                            }
                            variant={expert.status === 'running' ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area - Fixed at bottom */}
              <Box sx={{ p: { xs: 1, sm: 2 }, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                {/* Pending Attachments Preview */}
                {pendingAttachments.length > 0 && (
                  <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {pendingAttachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        icon={getAttachmentIcon(attachment.attachment_type)}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                              {attachment.original_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({formatFileSize(attachment.size)})
                            </Typography>
                          </Box>
                        }
                        onDelete={() => handleRemoveAttachment(attachment.id)}
                        deleteIcon={<CloseIcon fontSize="small" />}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          maxWidth: 280,
                          '& .MuiChip-deleteIcon': {
                            fontSize: 18,
                            marginRight: '4px',
                            color: 'action.active',
                            '&:hover': {
                              color: 'error.main',
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  style={{ display: 'none' }}
                  accept=".txt,.md,.json,.csv,.py,.js,.ts,.jsx,.tsx,.html,.css,.xml,.yaml,.yml,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.svg"
                />
                
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-start', flexDirection: 'column' }}>
                  {/* Voice overlay */}
                  {isVoiceOn && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <VoiceOverlay voiceState={voiceState} interimTranscript={interimTranscript} />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-start', width: '100%' }}>
                  {/* Attach File Button */}
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isBusy || isUploading || systemBuilderAccess.disabled}
                    size={isSmallMobile ? 'small' : 'medium'}
                    sx={{ mt: 0.5, p: { xs: 0.5, sm: 1 } }}
                    title={systemBuilderAccess.disabled ? systemBuilderAccess.blockedMessage : 'Attach file'}
                  >
                    {isUploading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AttachFileIcon />
                    )}
                  </IconButton>
                  
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    inputRef={chatInputRef}
                    size={isSmallMobile ? 'small' : 'medium'}
                    placeholder={systemBuilderAccess.disabled ? systemBuilderAccess.blockedMessage : isVoiceOn ? "Recording voice message..." : (pendingAttachments.length > 0 
                      ? "Add a message about your files..." 
                      : isSmallMobile ? "Ask the Architect..." : "Ask the Architect to design your system...")}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    disabled={isBusy || systemBuilderAccess.disabled}
                    sx={{
                      '& .MuiInputBase-root': {
                        py: { xs: 0.75, sm: 1 },
                      },
                    }}
                  />

                  {/* Mic button */}
                  <MicButton
                    voiceState={voiceState}
                    isRecordingSupported={isRecordingSupported}
                    disabled={voiceButtonDisabled}
                    disabledReason={voiceButtonDisabledReason}
                    onClick={toggleVoice}
                    size={isSmallMobile ? 'small' : 'medium'}
                    sx={{ mt: 0.5, flexShrink: 0 }}
                  />

                  {/* Voice settings */}
                  {isTTSSupported && (
                    <VoiceSettings
                      voices={voices}
                      selectedVoice={selectedVoice}
                      onVoiceChange={setSelectedVoice}
                      voiceRate={voiceRate}
                      onRateChange={setVoiceRate}
                      disabled={isVoiceOn}
                      sx={{ mt: 0.5 }}
                    />
                  )}

                  {isBusy ? (
                    isExecuting ? (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleStopExecution}
                      disabled={!currentExecutionId}
                      size={isSmallMobile ? 'small' : 'medium'}
                      sx={{
                        minWidth: { xs: 60, sm: 100 },
                        height: { xs: 40, sm: 56 },
                        borderWidth: 2,
                        px: { xs: 1, sm: 2 },
                        '&:hover': { borderWidth: 2 },
                      }}
                      startIcon={!isSmallMobile && <StopIcon />}
                    >
                      {isSmallMobile ? <StopIcon /> : 'Stop'}
                    </Button>
                    ) : (
                    <IconButton
                      disabled
                      size={isSmallMobile ? 'small' : 'medium'}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                      title="Preparing request"
                    >
                      <CircularProgress size={20} />
                    </IconButton>
                    )
                  ) : (
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={(!inputMessage.trim() && pendingAttachments.length === 0) || systemBuilderAccess.disabled}
                      size={isSmallMobile ? 'small' : 'medium'}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                      title={systemBuilderAccess.disabled ? systemBuilderAccess.blockedMessage : 'Send'}
                    >
                      <SendIcon />
                    </IconButton>
                  )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Resizable Divider/Handle - Hidden on Mobile */}
          {!isMobile && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                width: 8,
                cursor: 'col-resize',
                backgroundColor: isDragging ? 'primary.main' : 'transparent',
                transition: isDragging ? 'none' : 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&::after': {
                  content: '""',
                  width: 4,
                  height: 40,
                  backgroundColor: isDragging ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  transition: isDragging ? 'none' : 'background-color 0.2s',
                },
                '&:hover::after': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
          )}

          {/* Right Side: Insights & System Overview */}
          <Box 
            sx={{ 
              flex: 1, 
              minWidth: isMobile ? 'auto' : 300,
              width: isMobile ? '100%' : 'auto',
              height: '100%',
              overflow: 'hidden',
              display: isMobile ? (mobileTab === 1 ? 'flex' : 'none') : 'flex',
              margin: 0,
              padding: 0,
            }}
          >
            <AIBuilderInsightsPanel 
                system={system}
                isStaff={isStaff}
                sessionData={snapshotView ? { ...currentSessionData, system_snapshot: snapshotView.snapshot } : currentSessionData}
                isHistoricalSession={isHistoricalSession}
                selectedMessageId={snapshotView?.messageId ?? null}
                builderPlanStatusView={builderPlanStatusView}
                builderPlanDetailView={builderPlanDetailView}
                builderPlanReviewView={builderPlanReviewView}
                builderPlanActionView={builderPlanActionView}
                builderPlanPreviewView={builderPlanPreviewView}
                builderPlanHealthView={builderPlanHealthView}
                builderPlanDiffView={builderPlanDiffView}
                builderPlanRevisionView={builderPlanRevisionView}
                onUseSuggestedReply={handleUseSuggestedReply}
                builderIntentLedger={builderIntentLedgerResponse?.ledger ?? null}
                isLoadingBuilderIntentLedger={isFetchingBuilderIntentLedger}
                activeBuilderArtifact={activeBuilderArtifact}
                isLoadingActiveBuilderArtifact={isFetchingActiveBuilderArtifact}
                onApproveActiveBuilderArtifact={handleApproveActiveBuilderArtifact}
                onApplyActiveBuilderArtifact={handleApplyActiveBuilderArtifact}
                activeBuilderArtifactAction={builderArtifactAction}
                onUpdateBuilderIntentItem={handleUpdateBuilderIntentItem}
                onAppendBuilderIntentLedgerEvent={handleAppendBuilderIntentLedgerEvent}
                onSelectSourceMessage={handleSelectLedgerSourceMessage}
                builderIntentLedgerNotice={builderIntentLedgerNotice}
                updatingBuilderIntentItemId={updatingBuilderIntentItemId}
                onExitSnapshot={() => setSnapshotView(null)}
                // Pass canvas props for interactive canvas in system preview
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onAutoLayout={onAutoLayout}
                expertsData={expertsData}
                addExpertToCanvas={addExpertToCanvas}
                setExpertFormOpen={setExpertFormOpen}
                selectedNode={selectedNode}
                setSelectedNode={setSelectedNode}
                setNodes={setNodes}
                setEdges={setEdges}
                refetchExperts={refetchExperts}
                validationIssues={validationIssues}
                onSaveCanvas={onSaveCanvas}
                isSavingCanvas={isSavingCanvas}
                onRunCanvas={onRunCanvas}
                isRunningCanvas={isRunningCanvas}
                canRunCanvas={canRunCanvas}
                runDisabledReason={runDisabledReason}
                expertRunStates={expertRunStates}
                onViewExpertRun={onViewExpertRun}
                nodeTypes={nodeTypes}
                onExpertUpdate={onExpertUpdate}
                isMobile={isMobile}
              />
          </Box>
      </Box>

      {/* Restore Checkpoint Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={handleRestoreCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="warning" />
          Restore to Checkpoint
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to restore the AI system to this checkpoint?
          </DialogContentText>
          {restoreMessage && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Checkpoint from: {restoreMessage.timestamp.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Snapshot type: {restoreMessage.snapshotMode}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                "{restoreMessage.content.substring(0, 150)}{restoreMessage.content.length > 150 ? '...' : ''}"
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will fully restore the system to this checkpoint. Experts, schedules, and settings added after this point will be removed. You can undo by restoring to a newer checkpoint.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestoreCancel} disabled={isRestoring}>
            Cancel
          </Button>
          <Button 
            onClick={handleRestoreConfirm} 
            variant="contained" 
            color="warning"
            disabled={isRestoring}
            startIcon={isRestoring ? <CircularProgress size={16} /> : <RestoreIcon />}
          >
            {isRestoring ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Result Snackbar */}
      <Snackbar
        open={restoreSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setRestoreSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setRestoreSnackbar(prev => ({ ...prev, open: false }))}
          severity={restoreSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {restoreSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIBuilder;
